import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  TowerControl, Pause, Clock, Plug, Plus, Upload, RefreshCw, Copy, Search,
  AlertTriangle, Target, Sparkles, Check, X, HelpCircle, ExternalLink,
  Settings, Trash2, Save, Filter, ChevronLeft, ChevronRight, Play, TrendingUp, ChevronDown,
  TrendingDown, Activity, Minus, Download
} from 'lucide-react';
import { resolveFilterShape, getBodyKeys, buildFilters, supportsFilters } from '../lib/filterShape';

const MAX_COLS = 8;
const MAX_LINES = 50;
const PAGE_SIZE = 200;
const API = '/api/watch-tower';

// Partner filter hints — mirrors Fetch Engine, uses the shared filterShape lib so
// the request body Watch Tower sends is byte-identical to a Fetch Engine pull.
function detectPartnerHints(cfg) {
  const shape = resolveFilterShape(cfg);
  const bodyKeys = getBodyKeys(shape);
  const dims = cfg?.dimensions || [];
  const metrics = cfg?.metrics || [];
  return {
    shape, bodyKeys, dimensions: dims, metrics,
    filters: dims.filter(d => d && d.filterable !== false),
    filterOperators: (cfg?.operator_vocabulary || []).map(o => o.wire),
  };
}

// ── small presentational helpers ──────────────────────────────────────────────
function StateDot({ state }) {
  if (state === 'auth') return <span className="badge badge-green" title="Authorized" style={dotStyle}><Check size={12} /></span>;
  if (state === 'unauth') return <span className="badge badge-red" title="Not found" style={dotStyle}><X size={12} /></span>;
  if (state === 'removed') return <span className="badge badge-red" title="Removed from store" style={dotStyle}><Trash2 size={12} /></span>;
  return <span className="badge badge-amber" title="Unverifiable" style={dotStyle}><HelpCircle size={12} /></span>;
}
const dotStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '2px 6px', borderRadius: '999px' };

function EnvBadge({ env }) {
  return env === 'web'
    ? <span className="badge badge-cyan">Web</span>
    : <span className="badge badge-muted">App</span>;
}

function StoreLink({ row }) {
  if (!row.store_url) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  return (
    <a href={row.store_url} target="_blank" rel="noopener noreferrer"
       style={{ color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
      <ExternalLink size={12} /> Store
    </a>
  );
}

function adsTxtUrl(row) {
  if (!row) return null;
  if (row.env === 'web') return row.bundle_id ? `https://${row.bundle_id}/ads.txt` : null;
  return row.developer_domain ? `https://${row.developer_domain}/app-ads.txt` : null;
}
function AdsTxtLink({ row }) {
  const url = adsTxtUrl(row);
  if (!url) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" title={url} style={{ color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
      <ExternalLink size={12} /> {row.env === 'web' ? 'ads.txt' : 'app-ads.txt'}
    </a>
  );
}

// Publisher = the developer domain. app-ads.txt is hosted there, and Watch Tower
// crawls each unique domain once, so the publisher is the real unit of coverage.
// A large share of bundles have no developer domain — em-dash, matching AdsTxtLink.
function PublisherCell({ row }) {
  const d = row.env === 'web' ? (row.bundle_id || '') : (row.developer_domain || '');
  if (!d) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  return (
    <span title={d} style={{ fontFamily: 'var(--font-mono)', fontSize: 12, display: 'inline-block',
                             maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                             verticalAlign: 'bottom' }}>{d}</span>
  );
}

function PrivacyLink({ row }) {
  const dom = row.privacy_policy_domain;
  if (!dom) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  const url = `https://${dom}/app-ads.txt`;
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
      <a href={url} target="_blank" rel="noreferrer" title={url}
         style={{ color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
        <ExternalLink size={12} /> {dom}
      </a>
      {row.resolved_via_privacy && (
        <span title="app-ads.txt was found on the privacy-policy domain, not the developer domain"
              style={{ fontSize: 9, fontWeight: 700, color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 4, padding: '0 5px', alignSelf: 'flex-start', whiteSpace: 'nowrap' }}>
          app-ads.txt found here
        </span>
      )}
    </div>
  );
}

function Pager({ meta, onPage }) {
  if (!meta || !meta.total) return null;
  const start = (meta.page - 1) * meta.pageSize + 1;
  const end = Math.min(meta.total, meta.page * meta.pageSize);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, fontSize: 12, color: 'var(--text-secondary)', flexWrap: 'wrap', gap: 8 }}>
      <span>Showing {start.toLocaleString()}–{end.toLocaleString()} of {meta.total.toLocaleString()}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button className="btn btn-sm btn-secondary" disabled={meta.page <= 1} onClick={() => onPage(meta.page - 1)}><ChevronLeft size={14} /></button>
        <span>Page {meta.page} of {meta.pages}</span>
        <button className="btn btn-sm btn-secondary" disabled={meta.page >= meta.pages} onClick={() => onPage(meta.page + 1)}><ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

function copyToClipboard(values, label) {
  const text = (values || []).filter(Boolean).join('\n');
  if (navigator.clipboard) navigator.clipboard.writeText(text);
  toast.success(`Copied ${values.length} ${label}`);
}

// Single Copy button with an IDs / domains dropdown. onPick(kind) is async and
// fetches the full filtered set from the server before copying (see call sites).
function CopyMenu({ onPick, disabled }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  async function pick(kind) {
    setOpen(false); setBusy(true);
    try { await onPick(kind); } catch (e) { toast.error(e?.response?.data?.error || 'Copy failed'); } finally { setBusy(false); }
  }
  const item = { display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' };
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button className="btn btn-sm btn-secondary" onClick={() => setOpen(o => !o)} disabled={disabled || busy}>
        {busy ? <span className="spinner" /> : <Copy size={14} />} Copy <span style={{ fontSize: 10, marginLeft: 2 }}>{'\u25be'}</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,0.45)', zIndex: 41, minWidth: 150, overflow: 'hidden' }}>
            <button style={item} onClick={() => pick('ids')} onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>Copy IDs</button>
            <button style={item} onClick={() => pick('domains')} onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>Copy domains</button>
          </div>
        </>
      )}
    </div>
  );
}

// Server errors are strings from our own handlers, but an OBJECT when the
// response comes from elsewhere (a proxy 502 body, say). Rendering one as a
// React child throws error #31 and blanks the page, so always coerce.
function errText(e, fallback) {
  const d = e && e.response && e.response.data;
  const v = (d && (d.error != null ? d.error : d.message)) != null
    ? (d.error != null ? d.error : d.message)
    : (e && e.message);
  if (typeof v === 'string' && v.trim()) return v;
  if (v && typeof v === 'object') {
    if (typeof v.message === 'string') return v.message;
    try { return JSON.stringify(v); } catch { /* fall through */ }
  }
  if (e && e.response && e.response.status) return `HTTP ${e.response.status}`;
  return fallback || 'Request failed';
}

const LINE_KINDS = [{ v: 'all', label: 'All lines' }, { v: 'demand', label: 'Demand lines' }, { v: 'yours', label: 'Your lines' }];
const GEO_NAMES = { us: 'United States', gb: 'United Kingdom', de: 'Germany', fr: 'France', jp: 'Japan', kr: 'South Korea', cn: 'China', in: 'India', sg: 'Singapore', hk: 'Hong Kong', tw: 'Taiwan', id: 'Indonesia', au: 'Australia', br: 'Brazil' };
const geoLabel = (g) => GEO_NAMES[g] || String(g || '').toUpperCase();
function lineKindOf(l) { return (l && l.kind === 'yours') ? 'yours' : 'demand'; }

// ─── Sort helpers ─────────────────────────────────────────────────────────────
// SortHeader: a clickable column header. For server-sorted tables onSort refetches;
// for the client-side diagnostic sample, sortRows() reorders the in-memory rows.
function sortRows(rows, col, dir) {
  if (!col || !rows || !rows.length) return rows;
  return [...rows].sort((a, b) => {
    let av = a[col], bv = b[col];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return dir === 'asc' ? cmp : -cmp;
  });
}
function SortHeader({ col, label, current, dir, onSort, style = {} }) {
  const active = current === col;
  return (
    <th onClick={() => onSort(col)} className="sortable" style={style} title={`Sort by ${label}`}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {label}
        <span className="sort-ind" style={{ color: active ? 'var(--cyan)' : 'var(--text-muted)' }}>
          {active ? (dir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </span>
    </th>
  );
}

function MiniStat({ label, value, color, sub }) {
  // Map the color prop (e.g. 'var(--red)') to the shared .stat-card color variant
  // so Watch Tower's summary cards match Bundle Tracker / Dashboard exactly.
  const m = typeof color === 'string' ? color.match(/--([a-z]+)/) : null;
  const variant = m && ['green', 'red', 'cyan', 'amber', 'purple'].includes(m[1]) ? m[1] : '';
  return (
    <div className={'stat-card' + (variant ? ' ' + variant : '')}>
      <div className="stat-label">{label}</div>
      <div className={'stat-value' + (variant ? ' ' + variant : '')}
           style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
      {sub ? <div className="stat-sub">{sub}</div> : null}
    </div>
  );
}

// ─── Line Checker ─────────────────────────────────────────────────────────────
// Save one sellers.json domain; its PUBLISHER entries become the crawl set. Then
// save lists of arbitrary ads.txt lines and see, per line and per publisher,
// whether each line is present in that publisher's file.
//
// The wording never implies authorization. These lines may belong to anyone, so
// "present in their file" is the only claim the data supports.

const LC_VIEWS = [
  { v: 'line',      label: 'By line' },
  { v: 'publisher', label: 'By publisher' },
];

function LineCheckerTab() {
  const [cfg, setCfg]         = useState(null);
  const [lists, setLists]     = useState([]);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);

  const [domainInput, setDomainInput] = useState('');
  const [editing, setEditing] = useState(null);   // { id, name, text } | null

  const [view, setView]       = useState('line');
  // null = the landing view (the lists themselves). Set = that list's results.
  const [openList, setOpenList] = useState(null);
  const [listId, setListId]   = useState('');
  const [selIds, setSelIds]   = useState([]);     // narrow to specific lines
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [sortCol, setSortCol] = useState('found');
  const [sortDir, setSortDir] = useState('desc');
  // Publishers for an opened line, fetched on demand and cached per line.
  const [drill, setDrill]     = useState({});

  const loadConfig = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([
        axios.get(`${API}/line-checker/config`),
        axios.get(`${API}/line-checker/lists`),
      ]);
      setCfg({ ...c.data, readableCount: l.data.readable || 0 });
      setLists(l.data.lists || []);
      if (c.data && c.data.config && c.data.config.sellers_domain) setDomainInput(c.data.config.sellers_domain);
    } catch (e) { setErr(errText(e, 'Could not load Line Checker settings')); }
  }, []);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const loadResults = useCallback(async () => {
    if (!cfg || !cfg.publishers) return;
    if (openList === null) return;   // on the landing view there is nothing to fetch
    setLoading(true);
    try {
      const p = new URLSearchParams({ view, search, sort: sortCol, dir: sortDir,
                                      page: String(page), pageSize: '25' });
      if (listId) p.set('listId', String(listId));
      if (selIds.length) p.set('lineIds', selIds.join(','));
      const { data: d } = await axios.get(`${API}/line-checker/results?${p}`);
      setData(d);
    } catch (e) { setErr(errText(e, 'Could not load results')); }
    setLoading(false);
  }, [cfg, view, listId, selIds, search, page, sortCol, sortDir, openList]);
  useEffect(() => { loadResults(); }, [loadResults]);

  const saveDomain = async () => {
    setBusy(true); setErr(null);
    try {
      const { data: d } = await axios.put(`${API}/line-checker/config`, { sellersDomain: domainInput.trim() });
      toast.success(`Loaded ${Number(d.publishers).toLocaleString()} publisher entries across ${Number(d.domains || 0).toLocaleString()} domains`);
      await loadConfig();
    } catch (e) { setErr(errText(e, 'Could not read that sellers.json')); }
    setBusy(false);
  };

  const refreshDomain = async () => {
    setBusy(true); setErr(null);
    try {
      const { data: d } = await axios.post(`${API}/line-checker/refresh`, {});
      toast.success(`Refreshed — ${Number(d.publishers).toLocaleString()} publisher entries across ${Number(d.domains || 0).toLocaleString()} domains`);
      await loadConfig();
    } catch (e) { setErr(errText(e, 'Refresh failed')); }
    setBusy(false);
  };

  const saveList = async () => {
    if (!editing) return;
    setBusy(true); setErr(null);
    try {
      await axios.put(`${API}/line-checker/lists`, {
        id: editing.id || undefined, name: editing.name, lines: editing.text,
      });
      toast.success('List saved — results appear after the next publisher check');
      setEditing(null);
      await loadConfig();
    } catch (e) { setErr(errText(e, 'Could not save the list')); }
    setBusy(false);
  };

  const openListEditor = async (id) => {
    try {
      const { data: d } = await axios.get(`${API}/line-checker/lists/${id}`);
      setEditing({ id: d.list.id, name: d.list.name, text: (d.lines || []).map(x => x.line).join('\n') });
    } catch (e) { setErr(errText(e, 'Could not open the list')); }
  };

  const deleteList = async (id) => {
    if (!window.confirm('Delete this list and its results?')) return;
    try { await axios.delete(`${API}/line-checker/lists/${id}`); await loadConfig(); if (String(listId) === String(id)) setListId(''); }
    catch (e) { setErr(errText(e, 'Could not delete the list')); }
  };

  const sortBy = (col) => {
    if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('desc'); }
    setPage(1);
  };
  const arrow = (col) => (sortCol === col ? (sortDir === 'asc' ? ' \u25b2' : ' \u25bc') : '');

  // Fetched only when a row is opened. Attaching these to every collapsed row is
  // what made the tab slow — thousands of objects to render a page of 25.
  const openLine = async (lineId) => {
    if (openRow === lineId) { setOpenRow(null); return; }
    setOpenRow(lineId);
    if (drill[lineId]) return;
    try {
      const { data: d } = await axios.get(`${API}/line-checker/line-publishers?lineId=${lineId}`);
      setDrill(p => ({ ...p, [lineId]: d }));
    } catch (e) { setDrill(p => ({ ...p, [lineId]: { publishers: [], total: 0, error: errText(e, 'Could not load') } })); }
  };

  // window.open is a plain browser navigation and carries NO Authorization
  // header — the token lives in axios.defaults, not a cookie — so the request
  // arrived unauthenticated and requireAuth rejected it. Fetch it as a blob
  // through axios instead, which does carry the header.
  const exportCsv = async () => {
    if (!listId) { toast.error('Choose a list first — CSV exports one list at a time'); return; }
    setBusy(true);
    try {
      const p = new URLSearchParams({ view, search, format: 'csv', listId: String(listId) });
      if (selIds.length) p.set('lineIds', selIds.join(','));
      const res = await axios.get(`${API}/line-checker/results?${p}`, { responseType: 'blob' });
      const name = (lists.find(l => String(l.id) === String(listId)) || {}).name || 'list';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      a.download = `line-checker-${String(name).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { setErr(errText(e, 'Could not export the CSV')); }
    setBusy(false);
  };

  const configured = !!(cfg && cfg.config && cfg.config.sellers_domain && cfg.publishers > 0);
  const limits = (cfg && cfg.limits) || { maxLists: 20, maxLinesPerList: 50 };
  const sum = data && data.summary;

  return (
    <div>
      {/* ── The sellers.json domain everything depends on ─────────────────── */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>sellers.json domain</div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 0, marginBottom: 10 }}>
          The publishers listed in this file are the ones your lines are checked against. Only PUBLISHER
          entries with a usable domain are kept — intermediaries and confidential sellers cannot be crawled
          for an app-ads.txt.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="input-field" value={domainInput} onChange={e => setDomainInput(e.target.value)}
                 placeholder="bidmachine.io" style={{ fontFamily: 'var(--font-mono)', maxWidth: 320 }} />
          <button className="btn btn-primary btn-sm" onClick={saveDomain} disabled={busy || !domainInput.trim()}>
            {busy ? <span className="spinner" /> : <Save size={14} />} Save &amp; load
          </button>
          {configured && (
            <button className="btn btn-secondary btn-sm" onClick={refreshDomain} disabled={busy}>
              <RefreshCw size={14} /> Re-fetch
            </button>
          )}
          {cfg && cfg.config && cfg.config.last_fetched && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {Number(cfg.publishers).toLocaleString()} publisher entries
              {cfg.publisherDomains ? ` across ${Number(cfg.publisherDomains).toLocaleString()} domains` : ''}
              {cfg.config.total_sellers ? ` · of ${Number(cfg.config.total_sellers).toLocaleString()} sellers in the file` : ''}
              {' \u00b7 '}loaded {cfg.config.last_fetched}
            </span>
          )}
        </div>
        {cfg && cfg.config && cfg.config.status === 'error' && cfg.config.error && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--red)' }}>
            <AlertTriangle size={13} /> {cfg.config.error}
          </div>
        )}
        {err && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--red)' }}><AlertTriangle size={13} /> {err}</div>}
      </div>

      {!configured && (
        <div className="card" style={{ textAlign: 'center', padding: 30 }}>
          <Filter size={24} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
          <div style={{ fontSize: 13, fontWeight: 600 }}>Add a sellers.json domain to begin</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 5 }}>
            Everything on this tab is checked against the publishers in that file.
          </div>
        </div>
      )}

      {configured && (
        <>
          {/* ── Lists ────────────────────────────────────────────────────── */}
          <div className="card" style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Line lists</div>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {lists.length} of {limits.maxLists} · up to {limits.maxLinesPerList} lines each
              </span>
              <span style={{ marginLeft: 'auto' }} />
              <button className="btn btn-secondary btn-sm" disabled={lists.length >= limits.maxLists}
                      onClick={() => setEditing({ id: null, name: '', text: '' })}>
                <Plus size={14} /> New list
              </button>
            </div>

            {lists.length === 0 && !editing && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                No lists yet. Create one and paste the ads.txt lines you want to look for.
              </div>
            )}

            {lists.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {lists.map(l => (
                  <span key={l.id} className="badge badge-muted"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px' }}>
                    <span style={{ cursor: 'pointer' }} onClick={() => openListEditor(l.id)}>{l.name} · {l.line_count}</span>
                    <X size={11} style={{ cursor: 'pointer' }} onClick={() => deleteList(l.id)} />
                  </span>
                ))}
              </div>
            )}

            {editing && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <input className="input-field" value={editing.name} placeholder="List name"
                       onChange={e => setEditing({ ...editing, name: e.target.value })}
                       style={{ maxWidth: 320, marginBottom: 8 }} />
                <textarea className="input-field" rows={8} value={editing.text}
                          onChange={e => setEditing({ ...editing, text: e.target.value })}
                          placeholder={'example.com, 12345, DIRECT, f08c47fec0942fa0\nother.com, 99, RESELLER'}
                          style={{ fontFamily: 'var(--font-mono)', fontSize: 12, width: '100%', boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm" onClick={saveList} disabled={busy || !editing.name.trim()}>
                    <Save size={14} /> Save list
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    One line per row. Results appear after the next nightly publisher check.
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* ── Landing: the lists themselves ────────────────────────────── */}
          {openList === null && lists.length > 0 && (
            <div className="card">
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Your lists</div>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 0, marginBottom: 12 }}>
                Open a list to see its results by line or by publisher. Coverage is measured against the{' '}
                {exact((cfg && cfg.readableCount) || 0)} publisher domains whose file could be read.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>List</th>
                      <th style={{ textAlign: 'right' }}>Lines</th>
                      <th style={{ textAlign: 'right' }}>Publishers reached</th>
                      <th style={{ textAlign: 'right' }}>Line matches</th>
                      <th style={{ textAlign: 'right' }}>Dropped</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {lists.map(l => (
                      <tr key={l.id} style={{ cursor: 'pointer' }}
                          onClick={() => { setOpenList(l.id); setListId(String(l.id)); setSelIds([]); setPage(1); setOpenRow(null); }}>
                        <td style={{ fontWeight: 600 }}>
                          <ChevronRight size={12} style={{ color: 'var(--text-muted)' }} /> {l.name}
                        </td>
                        <td style={{ textAlign: 'right' }}>{exact(l.line_count)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: l.publishers_covered ? 'var(--green)' : 'var(--text-muted)' }}
                            title="Distinct publishers carrying at least one line from this list">
                          {exact(l.publishers_covered || 0)}
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}
                            title="Total line-publisher matches — a publisher carrying three lines counts three times">
                          {exact(l.hits || 0)}
                        </td>
                        <td style={{ textAlign: 'right', color: l.dropped ? 'var(--amber)' : 'var(--text-muted)' }}>{exact(l.dropped || 0)}</td>
                        <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                          <button className="btn btn-sm btn-secondary" onClick={() => openListEditor(l.id)}><Settings size={12} /></button>
                          <button className="btn btn-sm btn-secondary" style={{ marginLeft: 6 }} onClick={() => deleteList(l.id)}><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Results ──────────────────────────────────────────────────── */}
          {openList !== null && sum && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
              {/* Entries and domains are different units. A publisher with three
                  seller IDs is three entries and ONE file, so the first card counts
                  entries (matching Supply Partner Coverage) while the three file
                  cards count domains — and each says which, or they look like they
                  should add up and don't. */}
              <MiniStat label="Seller entries in this sellers.json" value={exact(sum.entries != null ? sum.entries : sum.publishers)}
                        sub={`${exact(sum.publishers)} unique publisher domains`} />
              <MiniStat label="Files read" value={exact(sum.readable)} color="var(--green)"
                        sub="domains — the only ones a 'not found' can be judged against" />
              <MiniStat label="Could not be read" value={exact(sum.unreadable)} color="var(--amber)"
                        sub="domains — fetch failed, not evidence of absence" />
              <MiniStat label="Not checked yet" value={exact(sum.notChecked)} color="var(--text-secondary)"
                        sub={sum.lastChecked ? `domains — last check ${sum.lastChecked}` : 'awaiting the first check'} />
            </div>
          )}

          {openList !== null && (
          <div className="card">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
              <button className="btn btn-sm btn-secondary"
                      onClick={() => { setOpenList(null); setListId(''); setSelIds([]); setPage(1); setOpenRow(null); }}>
                <ChevronLeft size={13} /> All lists
              </button>
              <div className="tabs" style={{ margin: 0 }}>
                {LC_VIEWS.map(v => (
                  <button key={v.v} className={`tab ${view === v.v ? 'active' : ''}`}
                          onClick={() => { setView(v.v); setPage(1); setOpenRow(null); }}>{v.label}</button>
                ))}
              </div>
              <select className="select-field" value={listId}
                      style={{ width: 220, fontSize: 12, fontWeight: 600 }}
                      onChange={e => { setListId(e.target.value); setSelIds([]); setPage(1); setOpenRow(null); }}>
                <option value="">All lists ({lists.length})</option>
                {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.line_count})</option>)}
              </select>
              <input className="input-field" value={search} placeholder="Search line or publisher…"
                     onChange={e => { setSearch(e.target.value); setPage(1); }}
                     style={{ flex: '1 1 200px', minWidth: 160, fontSize: 12 }} />
              <button className="btn btn-secondary btn-sm" onClick={exportCsv} disabled={busy || !listId}
                      title={listId ? 'Download this list\u2019s results' : 'Choose a list first — CSV exports one list at a time'}>
                <Download size={13} /> CSV
              </button>
            </div>

            {selIds.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                Showing {selIds.length} selected line{selIds.length === 1 ? '' : 's'} ·{' '}
                <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => { setSelIds([]); setPage(1); }}>clear</span>
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ fontSize: 12 }}>
                {view === 'line' ? (
                  <thead>
                    <tr>
                      <th style={{ width: 28 }} />
                      <th style={{ cursor: 'pointer' }} onClick={() => sortBy('line')}>Line{arrow('line')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => sortBy('list')}>List{arrow('list')}</th>
                      <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => sortBy('found')}>Present in{arrow('found')}</th>
                      <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => sortBy('notFound')}>Not found in{arrow('notFound')}</th>
                      <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => sortBy('coverage')}
                          title="Present in, as a share of the publishers whose file we could read">Coverage{arrow('coverage')}</th>
                      <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => sortBy('dropped')}>Dropped{arrow('dropped')}</th>
                    </tr>
                  </thead>
                ) : (
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => sortBy('publisher')}>Publisher{arrow('publisher')}</th>
                      <th>Seller ID</th>
                      <th>Seller name</th>
                      <th>app-ads.txt</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => sortBy('found')}>Lines present{arrow('found')}</th>
                      <th style={{ textAlign: 'right' }}>Not found</th>
                      <th style={{ textAlign: 'right' }} title="Lines present, as a share of the lines in scope">Coverage</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => sortBy('checked')}>Checked{arrow('checked')}</th>
                    </tr>
                  </thead>
                )}
                <tbody>
                  {loading && <EmptyRow cols={6} text="Loading…" />}
                  {!loading && (!data || !data.rows || data.rows.length === 0) && (
                    <EmptyRow cols={7} text={lists.length ? 'No results yet — they appear after the next publisher check.' : 'Create a list to see results.'} />
                  )}
                  {!loading && data && data.rows && data.rows.map(r => {
                    const key = view === 'line' ? r.lineId : r.publisher;
                    const on = openRow === key;
                    return (
                      <React.Fragment key={key}>
                        <tr onClick={() => (view === 'line' ? openLine(r.lineId) : setOpenRow(on ? null : key))}
                            style={{ cursor: 'pointer' }}>
                          {view === 'line' ? (
                            <>
                              <td onClick={e => e.stopPropagation()}>
                                <input type="checkbox" checked={selIds.includes(r.lineId)}
                                       onChange={() => { setSelIds(p => p.includes(r.lineId) ? p.filter(x => x !== r.lineId) : [...p, r.lineId]); setPage(1); }} />
                              </td>
                              <td style={{ fontFamily: 'var(--font-mono)', maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={r.line}>
                                <ChevronRight size={11} style={{ color: 'var(--text-muted)', transform: on ? 'rotate(90deg)' : 'none' }} /> {r.line}
                              </td>
                              <td style={{ color: 'var(--text-secondary)' }}>{r.listName}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: r.found ? 'var(--green)' : 'var(--text-muted)' }}>{exact(r.found)}</td>
                              <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }} title="Counted only against publishers whose file we could read">{exact(r.notFound)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600,
                                           color: r.coverage == null ? 'var(--text-muted)'
                                                : r.coverage >= 50 ? 'var(--green)'
                                                : r.coverage > 0 ? 'var(--amber)' : 'var(--red)' }}>
                                {r.coverage == null ? '\u2014' : `${r.coverage}%`}
                              </td>
                              <td style={{ textAlign: 'right', color: r.dropped ? 'var(--amber)' : 'var(--text-muted)' }}
                                  title="Was present on an earlier check, absent now">{exact(r.dropped)}</td>
                            </>
                          ) : (
                            <>
                              <td style={{ fontFamily: 'var(--font-mono)' }}>
                                <ChevronRight size={11} style={{ color: 'var(--text-muted)', transform: on ? 'rotate(90deg)' : 'none' }} /> {r.publisher}
                              </td>
                              {/* Straight from the sellers.json this tab is configured against. */}
                              <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{r.sellerId || '—'}</td>
                              <td style={{ fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={r.sellerName || ''}>{r.sellerName || '—'}</td>
                              {/* stopPropagation, or opening the file also collapses the row. */}
                              <td onClick={e => e.stopPropagation()}>
                                <a href={`https://${r.publisher}/app-ads.txt`} target="_blank" rel="noopener noreferrer"
                                   style={{ color: 'var(--accent)', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                  <ExternalLink size={11} /> app-ads.txt
                                </a>
                              </td>
                              <td>
                                {r.status === 'read'
                                  ? <span className="badge badge-green">File read</span>
                                  : r.status === 'unreadable'
                                    ? <span className="badge badge-amber" title="The fetch failed — this is not evidence the lines are absent">Could not read</span>
                                    : <span className="badge badge-muted" title="Not crawled yet">Not checked</span>}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: r.found ? 'var(--green)' : 'var(--text-muted)' }}>{exact(r.found)}</td>
                              <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                                {r.notFound == null ? <span style={{ color: 'var(--text-muted)' }}>—</span> : exact(r.notFound)}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600,
                                           color: r.coverage == null ? 'var(--text-muted)'
                                                : r.coverage >= 50 ? 'var(--green)'
                                                : r.coverage > 0 ? 'var(--amber)' : 'var(--red)' }}
                                  title={r.coverage == null ? 'Their file could not be read, so there is nothing to measure' : undefined}>
                                {r.coverage == null ? '\u2014' : `${r.coverage}%`}
                              </td>
                              <td style={{ color: 'var(--text-secondary)' }}>{r.checkedAt || '\u2014'}</td>
                            </>
                          )}
                        </tr>
                        {on && (
                          <tr>
                            <td colSpan={view === 'publisher' ? 9 : 7} style={{ background: 'var(--bg-surface)', padding: '8px 12px' }}>
                              {view === 'line' ? (
                                <>
                                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                                    Publishers whose file contains this line
                                    {drill[r.lineId] && drill[r.lineId].total > (drill[r.lineId].publishers || []).length
                                      ? ` (showing ${(drill[r.lineId].publishers || []).length} of ${exact(drill[r.lineId].total)})` : ''}
                                  </div>
                                  {!drill[r.lineId]
                                    ? <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}><span className="spinner" /> Loading…</div>
                                    : (drill[r.lineId].publishers || []).length === 0
                                    ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not found in any publisher's file yet.</div>
                                    : (
                                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                        {(drill[r.lineId].publishers || []).map(p => (
                                          <span key={p.publisher_domain}
                                                className={`badge ${p.present ? 'badge-green' : 'badge-muted'}`}
                                                style={{ fontSize: 10, fontFamily: 'var(--font-mono)' }}
                                                title={p.present ? `Present \u00b7 last seen ${p.last_seen}` : `Dropped \u00b7 last present ${p.last_seen}`}>
                                            {p.publisher_domain}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                </>
                              ) : (
                                <>
                                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                                    Lines present in {r.publisher}
                                  </div>
                                  {(r.lines || []).length === 0
                                    ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                        {r.status === 'read' ? 'None of your lines are in this file.' : 'Nothing to show \u2014 the file was not read.'}
                                      </div>
                                    : (r.lines || []).map(l => (
                                      <div key={l.line_id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '3px 0', flexWrap: 'wrap' }}>
                                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, flex: 1, minWidth: 220 }}>{l.line}</span>
                                        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{l.list_name}</span>
                                        <span className={`badge ${l.present ? 'badge-green' : 'badge-muted'}`} style={{ fontSize: 10 }}>
                                          {l.present ? 'Present' : `Dropped ${l.last_seen}`}
                                        </span>
                                      </div>
                                    ))}
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager meta={data} onPage={setPage} />
          </div>
          )}
        </>
      )}
    </div>
  );
}

export default function WatchTower() {
  const [tab, setTab] = useState('summary');
  const [sources, setSources] = useState({ web: null, inapp: null });
  const [setupEnv, setSetupEnv] = useState('inapp');
  const [lines, setLines] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [showSetup, setShowSetup] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadEnv, setUploadEnv] = useState('inapp');
  const [running, setRunning] = useState(false);
  const [runNote, setRunNote] = useState(null);

  // ── load core ──────────────────────────────────────────────────────────────
  const loadSource = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/source`); setSources(data && typeof data === 'object' ? { web: data.web || null, inapp: data.inapp || null } : { web: null, inapp: null }); }
    catch (e) { /* table may be empty on first load */ }
  }, []);
  const loadLines = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/lines`); setLines(data || []); }
    catch (e) { setLines([]); }
  }, []);
  useEffect(() => { loadSource(); loadLines(); }, [loadSource, loadLines]);

  const pollRef = useRef(null);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startPoll = useCallback(() => {
    setRunning(true);
    if (pollRef.current) clearInterval(pollRef.current);
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries++;
      try {
        const { data } = await axios.get(`${API}/run-status`);
        if (data.status === 'done') {
          clearInterval(pollRef.current); pollRef.current = null; setRunning(false);
          setRunNote(data.note || null);
          toast.success(`Reconcile complete${data.bundles ? ` \u2014 ${Number(data.bundles).toLocaleString()} bundles checked` : ''}`);
          if (data.note) toast(data.note, { duration: 9000 });
          loadSource();
        } else if (data.status === 'error') {
          clearInterval(pollRef.current); pollRef.current = null; setRunning(false);
          toast.error(`Run failed: ${data.error || 'unknown error'}`);
        } else if (tries > 120) {
          clearInterval(pollRef.current); pollRef.current = null; setRunning(false);
          toast('Still running in the background \u2014 refresh shortly to see results.');
        }
      } catch (e) {
        if (tries > 120) { clearInterval(pollRef.current); pollRef.current = null; setRunning(false); }
      }
    }, 3000);
  }, [loadSource]);

  const runNow = async () => {
    try {
      const { data } = await axios.post(`${API}/run`);
      if (data.skipped) { toast('Configure a source or upload bundles first'); return; }
      toast.success('Reconcile started \u2014 this can take a few minutes');
      startPoll();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not start run'); }
  };

  const clearData = async () => {
    if (!window.confirm('Delete all fetched bundles and results for Watch Tower? Saved lines and the source config are kept.')) return;
    try {
      await axios.post(`${API}/reset`);
      toast.success('Fetched data cleared');
      loadSource();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not clear data'); }
  };

  const toggleEnv = async (env) => {
    const cur = sources[env];
    const willEnable = cur ? cur.enabled === false : true;
    try {
      const { data } = await axios.post(`${API}/toggle`, { env, enabled: willEnable });
      toast.success(`${env === 'web' ? 'Web' : 'In-App'} ${data.enabled ? 'resumed' : 'paused'}`);
      loadSource();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not change state'); }
  };

  const lastRun = [sources.inapp?.last_run, sources.web?.last_run].filter(Boolean).map(d => String(d).slice(0, 10)).sort().pop();
  const pausedEnvs = [['inapp', 'In-App'], ['web', 'Web']].filter(([k]) => sources[k] && sources[k].enabled === false).map(([, lbl]) => lbl);

  return (
    <div>
      <h1 className="page-title">Watch Tower</h1>
      <p className="page-subtitle">Reconcile the requests you receive against your saved ads.txt lines — Web &amp; in-app supply only (CTV not included)</p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '14px 0' }}>
        <span className="badge badge-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Clock size={13} /> Daily · 00:30 UTC{lastRun ? ` · last ${lastRun}` : ''}</span>
        {pausedEnvs.length > 0 && (
          <span className="badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'transparent', color: 'var(--amber)', border: '1px solid var(--amber)' }}><Pause size={12} /> {pausedEnvs.join(' & ')} paused</span>
        )}
        <span style={{ marginRight: 'auto' }} />
        <button className="btn btn-sm btn-secondary" onClick={clearData} disabled={running}><Trash2 size={14} /> Clear data</button>
        <button className="btn btn-sm btn-primary" onClick={runNow} disabled={running}>
          {running ? <span className="spinner" /> : <Play size={14} />} Run now
        </button>
      </div>

      {runNote && (
        <div className="card" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 12px', marginBottom: 12, borderColor: 'var(--border-bright)' }}>
          <Plug size={14} style={{ color: 'var(--cyan)', marginTop: 2, flexShrink: 0 }} />
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 1 }}><span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Last fetch:</span> {runNote}</div>
          <button onClick={() => setRunNote(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 0 }} title="Dismiss"><X size={14} /></button>
        </div>
      )}

      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab ${tab === 'summary' ? 'active' : ''}`} onClick={() => setTab('summary')}><Activity size={15} /> Summary</button>
        <button className={`tab ${tab === 'exposure' ? 'active' : ''}`} onClick={() => setTab('exposure')}><AlertTriangle size={15} /> Exposure</button>
        <button className={`tab ${tab === 'missed' ? 'active' : ''}`} onClick={() => setTab('missed')}><Target size={15} /> Missed Coverage</button>
        <button className={`tab ${tab === 'new' ? 'active' : ''}`} onClick={() => setTab('new')}><Sparkles size={15} /> New Today</button>
        <button className={`tab ${tab === 'opps' ? 'active' : ''}`} onClick={() => setTab('opps')}><TrendingUp size={15} /> Missed Opportunities</button>
        <button className={`tab ${tab === 'discovery' ? 'active' : ''}`} onClick={() => setTab('discovery')}><Search size={15} /> Your Line Discovery</button>
        <button className={`tab ${tab === 'linecheck' ? 'active' : ''}`} onClick={() => setTab('linecheck')}><Filter size={15} /> Line Checker</button>
        <button className={`tab ${tab === 'config' ? 'active' : ''}`} onClick={() => setTab('config')}><Settings size={15} /> Configuration &amp; Settings</button>
      </div>

      {tab === 'config' && (
        <>
          <div className="grid-2" style={{ marginBottom: 16 }}>
            <SourceCard env="inapp" label="In-App" source={sources.inapp}
              onConfigure={() => { setSetupEnv('inapp'); setShowSetup(true); loadConfigsOnce(configs, setConfigs); }}
              onUpload={() => { setUploadEnv('inapp'); setShowUpload(true); }} onToggle={() => toggleEnv('inapp')} />
            <SourceCard env="web" label="Web" source={sources.web}
              onConfigure={() => { setSetupEnv('web'); setShowSetup(true); loadConfigsOnce(configs, setConfigs); }}
              onUpload={() => { setUploadEnv('web'); setShowUpload(true); }} onToggle={() => toggleEnv('web')} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <LinesCard lines={lines} reload={loadLines} />
          </div>
          {showSetup && (
            <SourceSetup env={setupEnv} source={sources[setupEnv]} configs={configs}
              onClose={() => setShowSetup(false)}
              onSaved={() => { setShowSetup(false); loadSource(); }} />
          )}
          {showUpload && (
            <UploadPanel env={uploadEnv} onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); startPoll(); }} />
          )}
        </>
      )}
      {tab === 'summary' && <SummaryTab onGoTab={setTab} running={running} />}
      {tab === 'exposure' && <ExposureTab lines={lines} />}
      {tab === 'missed' && <MissedTab lines={lines} />}
      {tab === 'new' && <NewTodayTab lines={lines} />}
      {tab === 'opps' && <MissedOppsTab lines={lines} />}
      {tab === 'discovery' && <LineDiscoveryTab running={running} />}
      {tab === 'linecheck' && <LineCheckerTab />}
    </div>
  );
}

async function loadConfigsOnce(configs, setConfigs) {
  if (configs && configs.length) return;
  try { const { data } = await axios.get('/api/partner-configs'); setConfigs(Array.isArray(data) ? data : (data.configs || [])); }
  catch (e) { /* ignore */ }
}

// ── Source display card ────────────────────────────────────────────────────────
function SourceCard({ env, label, source, onConfigure, onUpload, onToggle }) {
  const configured = source && source.config_id;
  const paused = source ? source.enabled === false : false;
  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="card-title">{label} source</span>
        {configured && (
          <button className={`btn btn-sm ${paused ? 'btn-primary' : 'btn-secondary'}`} onClick={onToggle}
            title={paused ? `Resume the daily reconcile for ${label}` : `Pause the daily reconcile for ${label}`}>
            {paused ? <><Play size={13} /> Resume</> : <><Pause size={13} /> Pause</>}
          </button>
        )}
      </div>
      {configured ? (
        <>
          <div style={{ fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Plug size={15} style={{ color: 'var(--accent)' }} /> Config #{source.config_id}
            {paused && <span className="badge" style={{ background: 'transparent', color: 'var(--amber)', border: '1px solid var(--amber)', fontSize: 10 }}>Paused</span>}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0 0' }}>
            Dimension: <code>{source.bundle_dimension || '—'}</code> · Metric: <code>{source.inventory_metric || '—'}</code>
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Window: {source.fetch_window || 'yesterday'}{source.last_run ? ` · last run ${String(source.last_run).slice(0, 10)}` : ' · not run yet'}
          </p>
        </>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No {label} source configured yet. Connect a partner API config for {env === 'web' ? 'web / domain' : 'in-app bundle'} inventory, or upload identifiers manually.</p>
      )}
      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <button className="btn btn-sm btn-secondary" onClick={onConfigure}><Settings size={14} /> Configure</button>
        <button className="btn btn-sm btn-secondary" onClick={onUpload}><Upload size={14} /> Manual upload</button>
      </div>
    </div>
  );
}

function LinesCard({ lines, reload }) {
  const [name, setName] = useState('');
  const [line, setLine] = useState('');
  const [kind, setKind] = useState('demand');
  const [adding, setAdding] = useState(false);

  const add = async () => {
    if (!name.trim() || !line.trim()) return toast.error('Name and line required');
    try {
      await axios.post(`${API}/lines`, { name: name.trim(), line: line.trim(), kind });
      setName(''); setLine(''); setKind('demand'); setAdding(false); reload();
      toast.success('Line saved');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save line'); }
  };
  const del = async (id) => {
    try { await axios.delete(`${API}/lines/${id}`); reload(); } catch (e) { toast.error('Delete failed'); }
  };

  // Inline rename. PUT /lines/:id COALESCEs every field, so sending ONLY the name
  // leaves the line, its kind and its linked allow/block lists untouched.
  // Renaming is display-only — nothing keys on the name (line_presence keys on the
  // line id, Missed Coverage links on the list ids) — so this cannot disturb any
  // stored result.
  const [renaming, setRenaming] = useState(null);   // { id, value }
  const commitRename = async () => {
    if (!renaming) return;
    const next = String(renaming.value || '').trim();
    const original = (lines.find(x => x.id === renaming.id) || {}).name || '';
    setRenaming(null);
    if (!next || next === original) return;         // nothing to do
    try {
      await axios.put(`${API}/lines/${renaming.id}`, { name: next });
      reload();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not rename'); }
  };

  const demand = lines.filter(l => lineKindOf(l) === 'demand');
  const yours  = lines.filter(l => lineKindOf(l) === 'yours');

  const [btLists, setBtLists] = useState([]);
  const [editLine, setEditLine] = useState(null);
  useEffect(() => {
    axios.get('/api/bundles/lists').then(({ data }) => setBtLists(Array.isArray(data) ? data : [])).catch(() => setBtLists([]));
  }, []);
  const allowLists = btLists.filter(x => x.type === 'approved');
  const blockLists = btLists.filter(x => x.type === 'blocked');
  const listName = (id) => (btLists.find(x => String(x.id) === String(id)) || {}).name || 'List';
  const linkState = (l) => (l.linked_allow_list_id ? 1 : 0) + (l.linked_block_list_id ? 1 : 0);
  const dotColor = (l) => { const n = linkState(l); return n === 2 ? 'var(--green)' : n === 1 ? 'var(--amber)' : 'var(--text-muted)'; };
  const demandLinked = demand.filter(l => linkState(l) > 0);
  const demandNeed = demand.filter(l => linkState(l) === 0);
  const saveLinks = async (id, allowId, blockId) => {
    try {
      await axios.put(`${API}/lines/${id}`, { linkedAllowListId: allowId || null, linkedBlockListId: blockId || null });
      toast.success('Links saved');
      setEditLine(null);
      reload();
    } catch (e) { toast.error('Could not save links'); }
  };

  const groupHead = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, marginBottom: 9, display: 'flex', alignItems: 'center', gap: 7 };
  const countPill = { fontSize: 9.5, fontWeight: 700, background: 'var(--bg-elevated)', color: 'var(--text-secondary)', borderRadius: 20, padding: '1px 7px' };
  const gdot = (c) => ({ width: 6, height: 6, borderRadius: '50%', background: c, flexShrink: 0, display: 'inline-block' });

  const DemandRow = ({ l }) => {
    const n = linkState(l);
    const need = n === 0;
    return (
      <div onClick={() => setEditLine(l)} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 11px', borderRadius: 'var(--radius)', background: 'var(--bg-surface)', border: '1px ' + (need ? 'dashed' : 'solid') + ' var(--border)', marginBottom: 6, cursor: 'pointer' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor(l), flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {renaming && renaming.id === l.id ? (
            <input autoFocus className="input-field" value={renaming.value}
                   onClick={e => e.stopPropagation()}
                   onChange={e => setRenaming({ ...renaming, value: e.target.value })}
                   onBlur={commitRename}
                   onKeyDown={e => {
                     if (e.key === 'Enter') commitRename();
                     if (e.key === 'Escape') setRenaming(null);
                   }}
                   style={{ fontSize: 13, fontWeight: 600, padding: '2px 6px', height: 26, maxWidth: 260 }} />
          ) : (
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', cursor: 'text', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                 title="Click to rename"
                 onClick={e => { e.stopPropagation(); setRenaming({ id: l.id, value: l.name || '' }); }}>
              {l.name}
              <Save size={11} style={{ opacity: 0.35 }} />
            </div>
          )}
          <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.line}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
          {need
            ? <button className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); setEditLine(l); }}>Link now</button>
            : (<>
                {l.linked_allow_list_id && <span className="badge badge-green" style={{ fontSize: 10 }}>{listName(l.linked_allow_list_id)}</span>}
                {l.linked_block_list_id && <span className="badge badge-red" style={{ fontSize: 10 }}>{listName(l.linked_block_list_id)}</span>}
                {n === 1 && <span className="badge badge-amber" style={{ fontSize: 10 }}>1 of 2</span>}
                <ChevronRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              </>)}
        </div>
      </div>
    );
  };

  const LinkEditor = ({ l, onClose }) => {
    const [a, setA] = useState(l.linked_allow_list_id || '');
    const [b, setB] = useState(l.linked_block_list_id || '');
    const fldLabel = { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', fontWeight: 600 };
    return (
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(4,8,16,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-bright)', borderRadius: 'var(--radius-lg)', padding: 22, width: 'min(460px, 92vw)', boxShadow: '0 24px 60px rgba(0,0,0,0.55)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor(l) }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{l.name}</span>
          </div>
          <div style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', margin: '4px 0 18px' }}>{l.line}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            <label style={fldLabel}>Allowlist</label>
            <select className="select-field" value={a} onChange={e => setA(e.target.value)}>
              <option value="">Allowlist — none</option>
              {allowLists.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            <label style={fldLabel}>Blocklist</label>
            <select className="select-field" value={b} onChange={e => setB(e.target.value)}>
              <option value="">Blocklist — none</option>
              {blockLists.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 16 }}>Missed Coverage shows bundles carrying this line that are in neither list.</div>
          <div style={{ display: 'flex', gap: 9, justifyContent: 'space-between', alignItems: 'center' }}>
            <button className="btn btn-sm btn-secondary" style={{ color: 'var(--red)' }} onClick={() => { del(l.id); onClose(); }}><Trash2 size={12} /> Delete</button>
            <div style={{ display: 'flex', gap: 9 }}>
              <button className="btn btn-sm btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-sm btn-primary" onClick={() => saveLinks(l.id, a, b)}><Save size={13} /> Save links</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Saved ads.txt lines · {lines.length} / {MAX_LINES}</span>
        {!adding && lines.length < MAX_LINES &&
          <button className="btn btn-sm btn-secondary" onClick={() => setAdding(true)}><Plus size={14} /> Add &amp; name</button>}
      </div>
      {adding && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="input-field" placeholder="Name (e.g. PubMatic Direct)" value={name} onChange={e => setName(e.target.value)} style={{ flex: 1 }} />
            <select className="select-field" value={kind} onChange={e => setKind(e.target.value)} style={{ width: 150 }}>
              <option value="demand">Demand line</option>
              <option value="yours">Your line</option>
            </select>
          </div>
          <input className="input-field" placeholder="pubmatic.com, 157150, DIRECT" value={line} onChange={e => setLine(e.target.value)} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-sm btn-primary" onClick={add}><Save size={13} /> Save</button>
            <button className="btn btn-sm btn-secondary" onClick={() => { setAdding(false); setName(''); setLine(''); setKind('demand'); }}>Cancel</button>
          </div>
        </div>
      )}
      {lines.length === 0 && !adding && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No lines saved yet.</div>}
      {lines.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 22 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, paddingBottom: 13, marginBottom: 15, borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 3, background: 'var(--cyan)', boxShadow: '0 0 12px color-mix(in srgb, var(--cyan) 45%, transparent)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 9, lineHeight: 1.1 }}>
                  Demand lines
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'var(--cyan-dim)', color: 'var(--cyan)' }}>{demand.length}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '5px 0 0', lineHeight: 1.45 }}>
                  <span style={{ color: 'var(--green-soft)', fontWeight: 700 }}>{demandLinked.length} linked</span> · <span style={{ color: 'var(--amber)', fontWeight: 700 }}>{demandNeed.length} need linking</span> — link a line to compare it in Missed Coverage.
                </div>
              </div>
            </div>
            {demandLinked.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ ...groupHead, color: 'var(--green-soft)' }}><span style={gdot('var(--green)')} />Linked <span style={countPill}>{demandLinked.length}</span></div>
                {demandLinked.map(l => <DemandRow key={l.id} l={l} />)}
              </div>
            )}
            {demandNeed.length > 0 && (
              <div>
                <div style={{ ...groupHead, color: 'var(--amber)' }}><span style={gdot('var(--amber)')} />Needs linking <span style={countPill}>{demandNeed.length}</span> <span style={{ fontSize: 9.5, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0 }}>· won't appear in Missed Coverage</span></div>
                {demandNeed.map(l => <DemandRow key={l.id} l={l} />)}
              </div>
            )}
            {demand.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No demand lines yet.</div>}
          </div>
          <div style={{ minWidth: 0, borderLeft: '1px solid var(--border)', paddingLeft: 22 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, paddingBottom: 13, marginBottom: 15, borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 3, background: 'var(--green)', boxShadow: '0 0 12px color-mix(in srgb, var(--green) 40%, transparent)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 9, lineHeight: 1.1 }}>
                  Your lines
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'var(--green-dim)', color: 'var(--green-soft)' }}>{yours.length}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '5px 0 0', lineHeight: 1.45 }}>Your own ads.txt lines — authorized inventory. No partner lists to attach.</div>
              </div>
            </div>
            {yours.map(l => (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 'var(--radius)', background: 'var(--bg-surface)', border: '1px solid var(--border)', marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {renaming && renaming.id === l.id ? (
                    <input autoFocus className="input-field" value={renaming.value}
                           onClick={e => e.stopPropagation()}
                           onChange={e => setRenaming({ ...renaming, value: e.target.value })}
                           onBlur={commitRename}
                           onKeyDown={e => {
                             if (e.key === 'Enter') commitRename();
                             if (e.key === 'Escape') setRenaming(null);
                           }}
                           style={{ fontSize: 13, fontWeight: 600, padding: '2px 6px', height: 26, maxWidth: 260 }} />
                  ) : (
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', cursor: 'text', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                         title="Click to rename"
                         onClick={e => { setRenaming({ id: l.id, value: l.name || '' }); }}>
                      {l.name}
                      <Save size={11} style={{ opacity: 0.35 }} />
                    </div>
                  )}
                  <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.line}</div>
                </div>
                <Trash2 size={13} style={{ cursor: 'pointer', color: 'var(--text-secondary)', flexShrink: 0 }} onClick={() => del(l.id)} />
              </div>
            ))}
            {yours.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No lines of your own yet.</div>}
          </div>
        </div>
      )}
      {editLine && <LinkEditor l={editLine} onClose={() => setEditLine(null)} />}
    </div>
  );
}

// ── Source setup form ──────────────────────────────────────────────────────────
function SourceSetup({ env = 'inapp', source, configs, onClose, onSaved }) {
  const [configId, setConfigId] = useState(source?.config_id ? String(source.config_id) : '');
  const [dim, setDim]       = useState(source?.bundle_dimension || '');
  const [metric, setMetric] = useState(source?.inventory_metric || '');
  const [pdim, setPdim]     = useState(source?.partner_dimension || '');
  const [selParts, setSelParts] = useState(Array.isArray(source?.selected_partners) ? source.selected_partners : []);
  const [seenParts, setSeenParts] = useState([]);   // discovered by past runs
  const [partIdInput, setPartIdInput]     = useState('');
  const [partNameInput, setPartNameInput] = useState('');
  // Entries are { id, name }. Legacy plain strings are upgraded on load so an
  // older saved config keeps working.
  const normPart = (p) => (typeof p === 'string' ? { id: p, name: p }
                        : { id: String((p && p.id) || ''), name: String((p && p.name) || (p && p.id) || '') });
  const addPart = () => {
    const id = partIdInput.trim();
    const nm = partNameInput.trim() || id;
    if (!id) return;
    setSelParts(list => (list.some(x => normPart(x).id === id) ? list : [...list, { id, name: nm }]));
    setPartIdInput(''); setPartNameInput('');
  };
  useEffect(() => {
    axios.get(`${API}/partners?env=all`)
      .then(({ data }) => setSeenParts((data.partners || []).map(p => p.partner)))
      .catch(() => {});
  }, []);
  const [win, setWin]       = useState(source?.fetch_window || 'yesterday');
  const [enabled, setEnabled] = useState(source?.enabled !== false);
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState('');
  const [bodyObj, setBodyObj]   = useState(source?.custom_body && typeof source.custom_body === 'object' ? source.custom_body : {});
  const [jsonText, setJsonText] = useState(source?.custom_body ? JSON.stringify(source.custom_body, null, 2) : '{}');
  const [jsonErr, setJsonErr]   = useState('');
  const [activeFilters, setActiveFilters] = useState(Array.isArray(source?.filters) ? source.filters : []);

  const cfg   = configs.find(c => String(c.id) === String(configId));
  const hints = detectPartnerHints(cfg);
  const dims  = cfg?.dimensions || [];
  const mets  = cfg?.metrics || [];

  const writeBody = (obj) => { setBodyObj(obj); setJsonText(JSON.stringify(obj, null, 2)); setJsonErr(''); };

  // Filters → request body, in the partner's exact wire shape (shared lib).
  const applyFiltersToBody = (fltrs) => {
    const parsed = { ...bodyObj };
    delete parsed.filters; delete parsed.filterBy;
    const shape = hints.shape;
    if (fltrs.length && supportsFilters(shape)) {
      const entries = buildFilters(shape, fltrs);
      if (shape === 'json-field-value-operator') parsed.filterBy = entries;
      else parsed.filters = entries;
    }
    writeBody(parsed);
  };
  const addFilter = (key) => {
    if (activeFilters.find(a => a.key === key)) return;
    const op = hints.filterOperators[0] || '=';
    const updated = [...activeFilters, { key, value: '', operator: op }];
    setActiveFilters(updated); applyFiltersToBody(updated);
  };
  const removeFilter = (key) => {
    const updated = activeFilters.filter(f => f.key !== key);
    setActiveFilters(updated); applyFiltersToBody(updated);
  };
  const updateFilter = (key, field, val) => {
    const updated = activeFilters.map(f => f.key === key ? { ...f, [field]: val } : f);
    setActiveFilters(updated); applyFiltersToBody(updated);
  };

  const applyPreset = (pid, list) => {
    setPresetId(pid);
    const p = (list || presets).find(x => String(x.id) === String(pid));
    if (!p) return;
    setActiveFilters(Array.isArray(p.filters) ? p.filters : []);
    writeBody(p.body || {}); // preset body already carries baked-in filters
  };

  useEffect(() => {
    if (!configId) { setPresets([]); setPresetId(''); return; }
    let active = true;
    axios.get(`/api/partner-configs/${configId}/presets`)
      .then(({ data }) => {
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        setPresets(list);
        if (list.length === 1 && !Object.keys(bodyObj).length) applyPreset(String(list[0].id), list);
      })
      .catch(() => { if (active) setPresets([]); });
    return () => { active = false; };
  }, [configId]);

  const onJsonEdit = (text) => {
    setJsonText(text);
    try { setBodyObj(JSON.parse(text)); setJsonErr(''); }
    catch { setJsonErr('Invalid JSON — fix before saving'); }
  };

  const changeConfig = (v) => {
    setConfigId(v); setDim(''); setMetric(''); setPdim(''); setPresetId(''); setActiveFilters([]); writeBody({});
  };

  const save = async () => {
    if (jsonErr) return toast.error('Fix the request body JSON first');
    setSaving(true);
    try {
      await axios.post(`${API}/source`, {
        env, mode: 'config', config_id: configId ? Number(configId) : null,
        bundle_dimension: dim, inventory_metric: metric, partner_dimension: pdim,
        selected_partners: selParts, fetch_window: win,
        custom_body: Object.keys(bodyObj).length ? bodyObj : null, filters: activeFilters, enabled,
      });
      toast.success('Source saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    setSaving(false);
  };

  const lbl = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)', margin: '0 0 4px', display: 'block', fontWeight: 600 };
  const shapeSupportsFilters = supportsFilters(hints.shape);

  return (
    <div className="card" style={{ marginBottom: 16, borderColor: 'var(--accent)' }}>
      <div className="card-header">
        <span className="card-title">Configure {env === 'web' ? 'Web' : 'In-App'} source</span>
        <button className="btn btn-sm btn-secondary" onClick={onClose}><X size={14} /></button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
        <div>
          <label style={lbl}>Partner API config</label>
          <select className="select-field" value={configId} onChange={e => changeConfig(e.target.value)}>
            <option value="">Select a config…</option>
            {configs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Bundle dimension</label>
          {dims.length
            ? <select className="select-field" value={dim} onChange={e => setDim(e.target.value)}>
                <option value="">Select…</option>
                {dims.map((d, i) => <option key={i} value={d.key || d}>{d.label || d.key || d}</option>)}
              </select>
            : <input className="input-field" value={dim} onChange={e => setDim(e.target.value)} placeholder="e.g. app_bundle_id" />}
        </div>
        <div>
          <label style={lbl}>Inventory metric</label>
          {mets.length
            ? <select className="select-field" value={metric} onChange={e => setMetric(e.target.value)}>
                <option value="">Select…</option>
                {mets.map((m, i) => <option key={i} value={m.key || m}>{m.label || m.key || m}</option>)}
              </select>
            : <input className="input-field" value={metric} onChange={e => setMetric(e.target.value)} placeholder="e.g. inventory" />}
        </div>
        <div>
          <label style={lbl}>Supply partner dimension <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span></label>
          {dims.length
            ? <select className="select-field" value={pdim} onChange={e => setPdim(e.target.value)}>
                <option value="">Not tracked</option>
                {dims.map((d, i) => <option key={i} value={d.key || d}>{d.label || d.key || d}</option>)}
              </select>
            : <input className="input-field" value={pdim} onChange={e => setPdim(e.target.value)} placeholder="e.g. publisher / ssp / supply_partner" />}
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.4 }}>
            Whichever dimension names the supply partner in this report. Enables Supply Partner Coverage.
          </div>
        </div>

        {/* Which partners to analyse. The discovered list only fills after a nightly
            run, so names can also be typed in ahead of the first one. */}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl}>Supply partners to analyse <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional — empty means all)</span></label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <input className="input-field" placeholder="ID from ad server (e.g. 1138)" value={partIdInput}
                   onChange={e => setPartIdInput(e.target.value)}
                   onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPart(); } }}
                   style={{ flex: '0 1 200px' }} />
            <input className="input-field" placeholder="Partner name (e.g. Verve New)" value={partNameInput}
                   onChange={e => setPartNameInput(e.target.value)}
                   onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPart(); } }}
                   style={{ flex: '1 1 220px' }} />
            <button type="button" className="btn btn-secondary" onClick={addPart}>Add</button>
          </div>
          {selParts.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {selParts.map(p => {
                const n = normPart(p);
                return (
                  <span key={n.id} className="badge" style={{ background: 'var(--accent)', color: 'var(--on-accent)', cursor: 'pointer' }}
                        title="Remove" onClick={() => setSelParts(list => list.filter(x => normPart(x).id !== n.id))}>
                    {n.id}{n.name && n.name !== n.id ? ` \u2192 ${n.name}` : ''} &times;
                  </span>
                );
              })}
            </div>
          )}
          {seenParts.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Seen in past runs — click to add:</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {seenParts.filter(id => !selParts.some(x => normPart(x).id === id)).map(id => (
                  <span key={id} className="badge badge-muted" style={{ cursor: 'pointer' }}
                        title="Adds the ID — then type its name beside it and press Add to map it"
                        onClick={() => setPartIdInput(id)}>+ {id}</span>
                ))}
              </div>
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.4 }}>
            The ad server sends opaque IDs; the name is what matches a source in the sellers.json DB.
            Leave empty to analyse every partner.
          </div>
        </div>
        <div>
          <label style={lbl}>Fetch window</label>
          <select className="select-field" value={win} onChange={e => setWin(e.target.value)}>
            <option value="yesterday">Yesterday</option>
            <option value="rolling7">Rolling 7 days</option>
          </select>
        </div>
        {configId && (
          <div>
            <label style={lbl}>Load from Fetch Engine preset</label>
            {presets.length
              ? <select className="select-field" value={presetId} onChange={e => applyPreset(e.target.value)}>
                  <option value="">Select a preset…</option>
                  {presets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              : <div style={{ fontSize: 12, color: 'var(--text-secondary)', paddingTop: 8 }}>No saved presets — build below or paste JSON.</div>}
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Filter size={13} style={{ color: 'var(--amber)' }} />
          <span style={{ ...lbl, margin: 0 }}>Filters</span>
          {activeFilters.length > 0 && <span className="badge badge-cyan" style={{ fontSize: 10 }}>{activeFilters.length} active</span>}
          {configId && !shapeSupportsFilters && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>— this partner filters via URL params, not the request body</span>}
        </div>
        {!configId && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Select a config to see its filterable fields.</div>}
        {configId && shapeSupportsFilters && hints.filters.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>This config has no filterable fields.</div>}
        {configId && shapeSupportsFilters && hints.filters.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 8 }}>
            {hints.filters.map((f) => {
              const fkey = f.key || f;
              const flabel = f.label || f.key || f;
              const active = activeFilters.find(a => a.key === fkey);
              const dimOps = (f.operators && f.operators.length) ? hints.filterOperators.filter(w => f.operators.includes(w)) : hints.filterOperators;
              const op = (active && active.operator) || hints.filterOperators[0] || '=';
              return (
                <div key={fkey} style={{ padding: '10px 12px', borderRadius: 'var(--radius-sm, 6px)',
                  background: active ? 'color-mix(in srgb, var(--cyan) 7%, transparent)' : 'var(--bg-elevated, var(--bg-surface))',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: active ? 'var(--accent)' : 'var(--text-primary)', flex: 1 }}>{flabel}</span>
                    <code style={{ fontSize: 10, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>{fkey}</code>
                    {active
                      ? <button className="btn btn-sm btn-secondary" style={{ padding: '2px 6px' }} onClick={() => removeFilter(fkey)} title="Remove"><X size={11} /></button>
                      : <button className="btn btn-sm btn-secondary" style={{ padding: '2px 8px' }} onClick={() => addFilter(fkey)} title="Add"><Plus size={12} /></button>}
                  </div>
                  {active && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      {dimOps.length > 0 && (
                        <select className="select-field" style={{ width: 96, fontSize: 11, padding: '4px 6px' }}
                          value={op} onChange={e => updateFilter(fkey, 'operator', e.target.value)}>
                          {dimOps.map(w => <option key={w} value={w}>{w}</option>)}
                        </select>
                      )}
                      <input className="input-field" style={{ flex: 1, minWidth: 0, fontSize: 11, fontFamily: 'var(--font-mono)' }}
                        placeholder={f.hint || 'values, comma-separated'} value={active.value || ''}
                        onChange={e => updateFilter(fkey, 'value', e.target.value)} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <label style={lbl}>Request body (auto-built · editable){jsonErr && <span style={{ color: 'var(--red)', textTransform: 'none', fontWeight: 400 }}> · {jsonErr}</span>}</label>
        <textarea className="input-field" rows={7} value={jsonText} onChange={e => onJsonEdit(e.target.value)}
          style={{ fontFamily: 'var(--font-mono)', fontSize: 12, lineHeight: 1.5, borderColor: jsonErr ? 'var(--red)' : undefined }} />
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>Filters above write into this body in your partner's exact format. The daily fetch substitutes your window's dates into <code>{'{date_from}'}</code> / <code>{'{date_to}'}</code>.</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-primary)' }}>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} /> Enabled (daily auto-fetch)
        </label>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !!jsonErr}>{saving ? <span className="spinner" /> : <Save size={14} />} Save source</button>
      </div>
    </div>
  );
}

// ── Manual upload ───────────────────────────────────────────────────────────────
function UploadPanel({ env = 'inapp', onClose, onDone }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const identifiers = text.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    if (!identifiers.length) return toast.error('Paste some bundle IDs or domains');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/upload`, { identifiers, env });
      toast.success(`Added ${data.added} identifiers`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.error || 'Upload failed'); }
    setBusy(false);
  };
  return (
    <div className="card" style={{ marginBottom: 16, borderColor: 'var(--accent)' }}>
      <div className="card-header">
        <span className="card-title">Manual upload — bundle IDs / domains</span>
        <button className="btn btn-sm btn-secondary" onClick={onClose}><X size={14} /></button>
      </div>
      <textarea className="input-field" rows={5} value={text} onChange={e => setText(e.target.value)}
        placeholder={'com.example.app\n284035177\nexample.com'} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
      <div style={{ marginTop: 10 }}>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={busy}>{busy ? <span className="spinner" /> : <Upload size={14} />} Upload &amp; reconcile</button>
      </div>
    </div>
  );
}

// ── Summary tab ─────────────────────────────────────────────────────────────
// The tool's front door. Everything here is one GET /summary; the page holds no
// derived state of its own so a number on screen always matches one the server
// stood behind. Loading / error / not-ready / no-baseline are ALL explicit states —
// a front door that renders blank while it decides is worse than one that says why.

const SUM_WINDOWS   = [{ v: 1, label: 'Since yesterday' }, { v: 7, label: 'Last 7 days' }];
const SUM_THRESHOLDS = [25, 50, 75];

function nfmt(n) {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e9) return (v / 1e9).toFixed(v % 1e9 === 0 ? 0 : 1) + 'B';
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(Math.abs(v) >= 1e8 ? 0 : 1) + 'M';
  if (Math.abs(v) >= 1e4) return (v / 1e3).toFixed(0) + 'K';
  return v.toLocaleString();
}
const exact = (n) => Number(n || 0).toLocaleString();

// Direction chip. `invert` flips the colour meaning for metrics where up is BAD
// (requests at risk, unverifiable) — green must never mean "more exposure".
function Delta({ delta, pct, invert, suffix }) {
  const d = Number(delta || 0);
  if (!d) return <span style={{ color: 'var(--text-muted)', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 3 }}><Minus size={11} /> no change</span>;
  const good = invert ? d < 0 : d > 0;
  const col = good ? 'var(--green)' : 'var(--red)';
  const Icon = d > 0 ? TrendingUp : TrendingDown;
  return (
    <span style={{ color: col, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
      <Icon size={12} />
      {d > 0 ? '+' : '\u2212'}{nfmt(Math.abs(d))}{suffix || ''}
      {pct != null ? <span style={{ opacity: 0.8, fontWeight: 400 }}>({pct > 0 ? '+' : ''}{pct}%)</span> : null}
    </span>
  );
}

// Collapsed by default. The header still carries the headline figure as a badge —
// a collapsed page that shows only titles forces you to open everything to learn
// whether anything happened, which defeats the point of collapsing.
function Panel({ title, sub, tone, right, badge, defaultOpen, children }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="card" style={{ marginBottom: 14, ...(tone ? { borderColor: tone } : null) }}>
      <div role="button" tabIndex={0} onClick={() => setOpen(o => !o)}
           onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
           style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer', userSelect: 'none' }}>
        <ChevronRight size={15} aria-hidden
                      style={{ marginTop: 2, flexShrink: 0, color: 'var(--text-secondary)',
                               transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 120ms ease' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
          {sub && open ? <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{sub}</div> : null}
        </div>
        {badge ? <div style={{ flexShrink: 0, display: 'flex', gap: 6, alignItems: 'center' }}>{badge}</div> : null}
      </div>
      {open && (
        <div style={{ marginTop: 12 }}>
          {right ? <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>{right}</div> : null}
          {children}
        </div>
      )}
    </div>
  );
}

// Chips naming the actual lines. Complete by default — a truncated list hides the
// one line you were looking for. Pass `max` only where space is genuinely tight
// (the nested per-app rows); the chips wrap rather than overflow.
function LineChips({ names, max }) {
  const list = names || [];
  const shown = max ? list.slice(0, max) : list;
  const rest = max ? list.slice(max) : [];
  if (!list.length) return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
      {shown.map(n => (
        <span key={n} className="badge badge-muted" style={{ fontSize: 10, fontFamily: 'var(--font-mono)' }}>{n}</span>
      ))}
      {rest.length > 0 && (
        <span className="badge badge-muted" style={{ fontSize: 10 }} title={rest.join(', ')}>+{rest.length} more</span>
      )}
    </span>
  );
}

// Which of YOUR lines moved, and what volume rode on each. Rows expand to the
// publishers responsible — knowing a line moved is only actionable once you know
// whose file moved it.
function LineRollup({ rows, verb, empty }) {
  const [openRow, setOpenRow] = useState(null);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="data-table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>Your line</th>
            <th style={{ textAlign: 'right' }}>Publishers</th>
            <th style={{ textAlign: 'right' }}>Apps</th>
            <th style={{ textAlign: 'right' }}>Requests</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).length === 0 && <EmptyRow cols={4} text={empty} />}
          {(rows || []).map(r => {
            const on = openRow === r.id;
            const pubs = r.publisherRows || [];
            return (
              <React.Fragment key={r.id}>
                <tr onClick={() => setOpenRow(on ? null : r.id)} style={{ cursor: 'pointer' }}
                    title={`Show the publishers that ${verb} ${r.name}`}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <ChevronRight size={12} style={{ color: 'var(--text-muted)', transform: on ? 'rotate(90deg)' : 'none', transition: 'transform 120ms ease' }} />
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.name}</span>
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{exact(r.publishers)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{exact(r.bundles)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} title={exact(r.requests)}>{nfmt(r.requests)}</td>
                </tr>
                {on && (
                  <tr>
                    <td colSpan={4} style={{ background: 'var(--bg-surface)', padding: '8px 12px' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                        Publishers that {verb} {r.name}
                        {r.publishers > pubs.length ? ` (showing ${pubs.length} of ${exact(r.publishers)} by requests)` : ''}
                      </div>
                      {pubs.map(pb => (
                        <div key={pb.publisher} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 0', flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, flex: 1, minWidth: 200 }}>{pb.publisher}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)', minWidth: 70, textAlign: 'right' }}>
                            {exact(pb.bundles)} app{pb.bundles === 1 ? '' : 's'}
                          </span>
                          <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', minWidth: 60, textAlign: 'right' }} title={exact(pb.requests)}>{nfmt(pb.requests)}</span>
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Who edited their file. Rows expand to the apps underneath, because the publisher
// is the unit of action but the app is what you actually block or allow.
function PublisherRollup({ rows, empty, linesLabel }) {
  const [openRow, setOpenRow] = useState(null);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="data-table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>Publisher</th>
            <th>{linesLabel || 'Lines changed'}</th>
            <th style={{ textAlign: 'right' }}>Apps</th>
            <th style={{ textAlign: 'right' }}>Requests</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).length === 0 && <EmptyRow cols={4} text={empty} />}
          {(rows || []).map(r => {
            const on = openRow === r.publisher;
            return (
              <React.Fragment key={r.publisher}>
                <tr onClick={() => setOpenRow(on ? null : r.publisher)} style={{ cursor: 'pointer' }}
                    title="Show the apps affected">
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <ChevronRight size={12} style={{ color: 'var(--text-muted)', transform: on ? 'rotate(90deg)' : 'none', transition: 'transform 120ms ease' }} />
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.publisher}</span>
                    </span>
                  </td>
                  <td><LineChips names={r.lines} /></td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{exact(r.bundles)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} title={exact(r.requests)}>{nfmt(r.requests)}</td>
                </tr>
                {on && (
                  <tr>
                    <td colSpan={4} style={{ background: 'var(--bg-surface)', padding: '8px 12px' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                        Top apps under {r.publisher}
                        {r.bundles > (r.topBundles || []).length ? ` (showing ${(r.topBundles || []).length} of ${exact(r.bundles)})` : ''}
                      </div>
                      {(r.topBundles || []).map(b => (
                        <div key={b.bundle_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 0', flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, minWidth: 190 }}>{b.bundle_id}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)', flex: 1, minWidth: 120 }}>{b.app_name || ''}</span>
                          <LineChips names={b.lines} max={3} />
                          <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', minWidth: 60, textAlign: 'right' }} title={exact(b.inventory)}>{nfmt(b.inventory)}</span>
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Bundle identity cell, shared by every list on this page.
function BundleCell({ row }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      <span title={row.bundle_id} style={{ fontFamily: 'var(--font-mono)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>{row.bundle_id}</span>
      {row.app_name ? <span style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>{row.app_name}</span> : null}
    </div>
  );
}

// Authorization is FOUR-valued, because verify_state is. Collapsing 'pending'
// (never crawled) and 'removed' (delisted) into "file unreadable" states a cause
// we did not observe — and on the New apps card most rows ARE pending, since an
// app discovered today is crawled on the NEXT run. Shared by Top 20 and New apps
// so the two cannot drift.
function AuthCell({ row, totalLines, setLabel }) {
  const st = row.state;
  if (st === 'pending')
    return <span className="badge badge-muted" title="Not crawled yet — authorization is unknown, not absent">Not checked yet</span>;
  if (st === 'removed')
    return <span className="badge badge-muted" title="No longer live on the store">Delisted</span>;
  if (st !== 'ok')
    return <span className="badge badge-amber" title="Their app-ads.txt could not be fetched, so authorization is unknown — this is not the same as being unauthorized">File unreadable</span>;

  const n = row.linesPresent || 0;
  const present = (row.lineNames || []).join(', ');
  const missing = (row.missingNames || []).join(', ');
  if (!totalLines)
    return <span className="badge badge-muted" title="No lines in the selected set">—</span>;
  if (n === 0)
    return <span className="badge badge-red" title={`None of your ${totalLines} ${setLabel} appear in this publisher's app-ads.txt.\nMissing: ${missing}`}>None of {totalLines}</span>;
  if (n === totalLines)
    return <span className="badge badge-green" title={`Present: ${present}`}>All {totalLines}</span>;
  return <span className="badge badge-amber" title={`Present: ${present}\nMissing: ${missing}`}>{n} of {totalLines}</span>;
}

function EmptyRow({ cols, text }) {
  return <tr><td colSpan={cols} style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12, padding: '14px 0' }}>{text}</td></tr>;
}

// Movers / flips / discoveries all render the same shape: identity, a number, a
// delta. One table keeps them visually consistent and keeps this file short.
function MoverTable({ rows, cap, valueLabel, valueOf, deltaOf, pctOf, invert, empty, extra, extraLabel }) {
  const list = (rows || []).slice(0, cap || 10);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="data-table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>Bundle</th>
            <th style={{ textAlign: 'right' }}>{valueLabel}</th>
            {deltaOf ? <th style={{ textAlign: 'right' }}>Change</th> : null}
            {extra ? <th>{extraLabel}</th> : null}
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && <EmptyRow cols={2 + (deltaOf ? 1 : 0) + (extra ? 1 : 0)} text={empty} />}
          {list.map(r => (
            <tr key={r.bundle_id}>
              <td><BundleCell row={r} /></td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} title={exact(valueOf(r))}>{nfmt(valueOf(r))}</td>
              {deltaOf ? <td style={{ textAlign: 'right' }}><Delta delta={deltaOf(r)} pct={pctOf ? pctOf(r) : null} invert={invert} /></td> : null}
              {extra ? <td>{extra(r)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SummaryTab({ onGoTab, running }) {
  const [win, setWin] = useState(1);
  const [lineKind, setLineKind] = useState('demand');
  const [threshold, setThreshold] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams({ window: String(win), lineKind, threshold: String(threshold) });
      const { data: d } = await axios.get(`${API}/summary?${params}`);
      setData(d);
    } catch (e) {
      setErr(errText(e, 'Could not load the summary'));
      setData(null);
    }
    setLoading(false);
  }, [win, lineKind, threshold]);
  useEffect(() => { load(); }, [load]);
  // A finished run changes every number on this page. Fire on the true->false
  // TRANSITION only: keying on `running === false` alone would also fire on mount,
  // duplicating the initial fetch.
  const wasRunning = useRef(running);
  useEffect(() => {
    if (wasRunning.current && !running) load();
    wasRunning.current = running;
  }, [running, load]);

  const controls = (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
      <div className="tabs" style={{ margin: 0 }}>
        {SUM_WINDOWS.map(w => (
          <button key={w.v} className={`tab ${win === w.v ? 'active' : ''}`} onClick={() => setWin(w.v)}>{w.label}</button>
        ))}
      </div>
      <span style={{ marginRight: 'auto' }} />
      <select className="select-field" value={lineKind} onChange={e => setLineKind(e.target.value)} style={{ width: 150, fontSize: 12 }}
              title="Which line set coverage is measured against">
        {LINE_KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
      </select>
      <select className="select-field" value={threshold} onChange={e => setThreshold(Number(e.target.value))} style={{ width: 190, fontSize: 12 }}
              title="Publishers are split above/below this coverage level">
        {SUM_THRESHOLDS.map(t => <option key={t} value={t}>Coverage split at {t}%</option>)}
      </select>
      <button className="btn btn-sm btn-secondary" onClick={load} disabled={loading}>
        {loading ? <span className="spinner" /> : <RefreshCw size={14} />} Refresh
      </button>
    </div>
  );

  if (loading && !data) return <div>{controls}<div className="card" style={{ textAlign: 'center', padding: 34, color: 'var(--text-secondary)', fontSize: 13 }}><span className="spinner" /> Loading summary…</div></div>;

  if (err) return (
    <div>{controls}
      <div className="card" style={{ borderColor: 'var(--red)', padding: 18 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertTriangle size={16} style={{ color: 'var(--red)', flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Could not load the summary</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{err}</div>
            <button className="btn btn-sm btn-secondary" style={{ marginTop: 10 }} onClick={load}><RefreshCw size={14} /> Try again</button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!data || data.ready === false) return (
    <div>{controls}
      <div className="card" style={{ textAlign: 'center', padding: 34 }}>
        <Activity size={26} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Nothing to summarise yet</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 5, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
          {(data && data.message) || 'No completed runs yet.'} Comparisons need two runs, so the change figures appear after the second one.
        </div>
        <button className="btn btn-sm btn-secondary" style={{ marginTop: 12 }} onClick={() => onGoTab && onGoTab('config')}>
          <Settings size={14} /> Configuration &amp; Settings
        </button>
      </div>
    </div>
  );

  const p  = data.period || {};
  const ac = data.accounting || {};
  const mv = data.movers || {};
  const pc = data.publisherCoverage || {};
  const fl = data.flips || {};
  const cc = data.concentration || {};
  const noBase = !data.hasBaseline;
  const setName = LINE_KINDS.find(k => k.v === data.lineKind);
  const lineSetLabel = (setName ? setName.label : 'lines').toLowerCase();

  return (
    <div>
      {controls}

      {/* What exactly is being compared. Run date and data date differ by a day, so
          say which one these numbers describe rather than letting the reader assume. */}
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <Clock size={13} />
        {noBase
          ? <span>Requests for <strong style={{ color: 'var(--text-primary)' }}>{p.dataDate}</strong> — only one run on record, so there is nothing to compare against yet.</span>
          : <span>
              Requests for <strong style={{ color: 'var(--text-primary)' }}>{p.dataDate}</strong> vs <strong style={{ color: 'var(--text-primary)' }}>{p.baseDataDate}</strong>
              {p.gapDays != null ? ` \u00b7 ${p.gapDays} day${p.gapDays === 1 ? '' : 's'} apart` : ''}
              {data.window === 7 && p.baseExact === false ? ' \u00b7 nearest run to 7 days back' : ''}
              {' \u00b7 '}coverage measured against {data.totalLines} {lineSetLabel}
            </span>}
      </div>

      {data.windowWarning && (
        <div className="card" style={{ borderColor: 'var(--amber)', padding: '9px 12px', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertTriangle size={14} style={{ color: 'var(--amber)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{data.windowWarning}</div>
        </div>
      )}

      {ac.legacySnapshotRows > 0 && (
        <div className="card" style={{ borderColor: 'var(--amber)', padding: '9px 12px', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <HelpCircle size={14} style={{ color: 'var(--amber)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {exact(ac.legacySnapshotRows)} snapshot row{ac.legacySnapshotRows === 1 ? '' : 's'} predate the change that records whether a bundle
            was in that day&rsquo;s fetch. For those, a bundle that stopped sending requests still shows its last known number rather than zero,
            so drops are under-reported until both compared runs are newer.
          </div>
        </div>
      )}

      {/* ── Headline ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
        <MiniStat label="Requests received" value={nfmt(data.requests.now)}
                  sub={noBase ? exact(data.requests.now) : `${data.requests.pct != null ? (data.requests.pct > 0 ? '+' : '') + data.requests.pct + '%' : 'no baseline'} vs ${p.baseDataDate}`} />
        <MiniStat label="Requests at risk" value={nfmt(data.atRisk.now)} color="var(--red)"
                  sub={`unauthorized on ${lineSetLabel}`} />
        <MiniStat label="Authorized bundles" value={exact(data.authorized.now)} color="var(--green)"
                  sub={noBase ? '' : `${data.authorized.delta >= 0 ? '+' : '\u2212'}${exact(Math.abs(data.authorized.delta))} vs baseline`} />
        <MiniStat label={`Publishers under ${data.threshold}%`} value={exact(pc.belowCount)} color="var(--amber)"
                  sub={`${nfmt(pc.belowRequests)} requests behind them`} />
      </div>

      {!noBase && (
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12, marginBottom: 16, padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Requests <Delta delta={data.requests.delta} pct={data.requests.pct} /></span>
          <span style={{ color: 'var(--text-secondary)' }}>At risk <Delta delta={data.atRisk.delta} pct={data.atRisk.pct} invert /></span>
          <span style={{ color: 'var(--text-secondary)' }}>Unverifiable <Delta delta={data.unverifiable.delta} invert /></span>
          <span style={{ color: 'var(--text-secondary)' }}>
            Top 10 concentration <strong style={{ color: 'var(--text-primary)' }}>{cc.top10Now != null ? cc.top10Now + '%' : '\u2014'}</strong>
            {cc.top10Prev != null ? <span style={{ color: 'var(--text-muted)' }}> (was {cc.top10Prev}%)</span> : null}
          </span>
        </div>
      )}

      {/* ── Line changes: the only section reporting something that HAPPENED ── */}
      {!noBase && (
        <Panel
          title="app-ads.txt line changes"
          tone={fl.lostCount > 0 ? 'var(--red)' : undefined}
          badge={<>
            {fl.lostCount > 0
              ? <span className="badge badge-red">{exact(fl.lostCount)} apps lost a line</span>
              : <span className="badge badge-muted">nothing removed</span>}
            {fl.gainedCount > 0 ? <span className="badge badge-green">{exact(fl.gainedCount)} gained</span> : null}
          </>}
          sub={`Publishers list the demand partners they authorize inside their app-ads.txt file. When one of your lines disappears from that file, their apps no longer declare your path as authorized — bids you send can be rejected as unauthorized inventory. Because the file is hosted per developer domain, one publisher editing one file changes every app they own at once.`}
        >
          {/* Removed */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--red)', marginBottom: 3 }}>
              Lines removed — authorization lost
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 9 }}>
              {fl.lostCount === 0
                ? 'No publisher removed any of your lines.'
                : <>
                    {exact(fl.lostPublishers)} publisher{fl.lostPublishers === 1 ? '' : 's'} removed at least one line,
                    affecting {exact(fl.lostCount)} app{fl.lostCount === 1 ? '' : 's'} and {nfmt(fl.lostRequests)} requests.
                    {fl.lostWholly > 0 ? <> <strong style={{ color: 'var(--red)' }}>{exact(fl.lostWholly)} of those now carry none of your lines at all.</strong></> : null}
                  </>}
            </div>
            {fl.lostCount > 0 && (
              <>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', fontWeight: 700, margin: '10px 0 5px' }}>
                  Which of your lines were dropped
                </div>
                <LineRollup rows={fl.byLine.lost} verb="removed" empty="None." />
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', fontWeight: 700, margin: '14px 0 5px' }}>
                  Which publishers removed / changed their file
                  {fl.byPublisher.lostTotal > (fl.byPublisher.lost || []).length
                    ? <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> — top {(fl.byPublisher.lost || []).length} of {exact(fl.byPublisher.lostTotal)} by requests</span>
                    : null}
                </div>
                <PublisherRollup rows={fl.byPublisher.lost} linesLabel="Lines removed" empty="None." />
              </>
            )}
          </div>

          {/* Added */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', marginBottom: 3 }}>
              Lines added — newly authorized
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 9 }}>
              {fl.gainedCount === 0
                ? 'No publisher added any of your lines.'
                : <>
                    {exact(fl.gainedPublishers)} publisher{fl.gainedPublishers === 1 ? '' : 's'} added at least one line,
                    affecting {exact(fl.gainedCount)} app{fl.gainedCount === 1 ? '' : 's'} and {nfmt(fl.gainedRequests)} requests.
                    {fl.gainedFreshly > 0 ? ` ${exact(fl.gainedFreshly)} of those had none of your lines before.` : ''}
                  </>}
            </div>
            {fl.gainedCount > 0 && (
              <>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', fontWeight: 700, margin: '10px 0 5px' }}>
                  Which of your lines appeared
                </div>
                <LineRollup rows={fl.byLine.gained} verb="added" empty="None." />
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', fontWeight: 700, margin: '14px 0 5px' }}>
                  Which publishers added / changed their file
                  {fl.byPublisher.gainedTotal > (fl.byPublisher.gained || []).length
                    ? <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> — top {(fl.byPublisher.gained || []).length} of {exact(fl.byPublisher.gainedTotal)} by requests</span>
                    : null}
                </div>
                <PublisherRollup rows={fl.byPublisher.gained} linesLabel="Lines added" empty="None." />
              </>
            )}
          </div>

          {/* Excluded, and why. A file that failed to load has no line data at all, so
              counting it as a removal would raise a false alarm on every timeout. */}
          {data.unverifiable.unreadableWasAuthorized > 0 && (
            <div style={{ marginTop: 16, padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-surface)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <HelpCircle size={14} style={{ color: 'var(--amber)', flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>
                  {exact(data.unverifiable.unreadableWasAuthorized)} app{data.unverifiable.unreadableWasAuthorized === 1 ? '' : 's'} excluded from the counts above
                </strong>
                {' — '}authorized on {p.baseDataDate}, but their app-ads.txt could not be read on {p.dataDate}
                ({nfmt(data.unverifiable.unreadableRequests)} requests). A file that fails to load carries no line data,
                so treating it as a removal would raise a false alarm every time a domain times out. Re-check them from the Exposure tab.
              </div>
            </div>
          )}
        </Panel>
      )}

      {/* ── Movers ──────────────────────────────────────────────────────────── */}
      {!noBase && (
        <Panel title="Request movement by app"
               badge={<>
                 <span className="badge badge-green">{exact(mv.upCount)} up</span>
                 <span className="badge badge-red">{exact(mv.downCount)} down</span>
                 {mv.stoppedCount > 0 ? <span className="badge badge-amber">{exact(mv.stoppedCount)} stopped</span> : null}
               </>}
               sub={`${exact(mv.upCount)} apps sent more than on ${p.baseDataDate}, ${exact(mv.downCount)} sent less, ${exact(mv.startedCount)} started sending for the first time and ${exact(mv.stoppedCount)} stopped entirely.`}>
          <div className="grid-2" style={{ gap: 14 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>Biggest increases</div>
              <MoverTable rows={mv.risers} cap={10} valueLabel="Requests"
                          valueOf={r => r.now} deltaOf={r => r.delta} pctOf={r => r.pct} empty="Nothing increased." />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--red)', marginBottom: 6 }}>Biggest drops</div>
              <MoverTable rows={mv.fallers} cap={10} valueLabel="Requests"
                          valueOf={r => r.now} deltaOf={r => r.delta} pctOf={r => r.pct} empty="Nothing dropped." />
            </div>
          </div>
          {(mv.stopped || []).length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--amber)', marginBottom: 6 }}>
                Stopped completely · {exact(mv.stoppedCount)}
                <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> — present on {p.baseDataDate}, absent from the {p.dataDate} fetch</span>
              </div>
              <MoverTable rows={mv.stopped} cap={10} valueLabel="Was" valueOf={r => r.prev} empty="None." />
            </div>
          )}
        </Panel>
      )}

      {/* ── Top bundles ─────────────────────────────────────────────────────── */}
      <Panel title="Top 20 apps by requests"
             badge={<span className="badge badge-muted">{nfmt(data.requests.now)} total</span>}
             sub={`"Your lines in their file" counts how many of the ${data.totalLines} ${lineSetLabel} appear in that app's publisher app-ads.txt. Hover a cell for the exact names. "File unreadable" means the file could not be fetched — unknown, not unauthorized.`}
             right={<button className="btn btn-sm btn-secondary" onClick={() => copyToClipboard((data.topBundles || []).map(r => r.bundle_id), 'bundle IDs')}><Copy size={13} /> Copy IDs</button>}>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ width: 34 }}>#</th>
                <th>Bundle</th>
                <th>Publisher</th>
                <th style={{ textAlign: 'right' }}>Requests</th>
                {!noBase && <th style={{ textAlign: 'right' }}>Change</th>}
                <th style={{ textAlign: 'center' }}>Your lines in their file</th>
                <th>Store</th>
              </tr>
            </thead>
            <tbody>
              {(data.topBundles || []).length === 0 && <EmptyRow cols={noBase ? 6 : 7} text="No bundles in the latest run." />}
              {(data.topBundles || []).map((r, i) => (
                <tr key={r.bundle_id}>
                  <td style={{ color: 'var(--text-muted)' }}>{i + 1}</td>
                  <td><BundleCell row={r} /></td>
                  <td><PublisherCell row={r} /></td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} title={exact(r.now)}>{nfmt(r.now)}</td>
                  {!noBase && <td style={{ textAlign: 'right' }}><Delta delta={r.delta} pct={r.pct} /></td>}
                  <td style={{ textAlign: 'center' }}>
                    <AuthCell row={r} totalLines={data.totalLines} setLabel={lineSetLabel} />
                  </td>
                  <td><StoreLink row={r} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* ── Publisher coverage ──────────────────────────────────────────────── */}
      <Panel title={`Publisher coverage against ${lineSetLabel}`}
             badge={<>
               <span className="badge badge-green">{exact(pc.aboveCount)} at or above {data.threshold}%</span>
               <span className="badge badge-amber">{exact(pc.belowCount)} below</span>
             </>}
             sub={`Ranked by requests, not coverage \u2014 a publisher at 40% carrying millions is worth more than twenty at 60% carrying nothing.`}
             right={<button className="btn btn-sm btn-secondary" onClick={() => onGoTab && onGoTab('opps')}><TrendingUp size={13} /> Open Missed Opportunities</button>}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
          <MiniStat label="Publishers analysed" value={exact(pc.publishers)} />
          <MiniStat label={`At or above ${data.threshold}%`} value={exact(pc.aboveCount)} color="var(--green)" sub={`${nfmt(pc.aboveRequests)} requests`} />
          <MiniStat label={`Below ${data.threshold}%`} value={exact(pc.belowCount)} color="var(--amber)" sub={`${nfmt(pc.belowRequests)} requests`} />
          <MiniStat label="Bundles fully covered" value={exact(data.fullCoverage.now)} color="var(--green)"
                    sub={noBase ? `all ${data.totalLines} lines present` : `${data.fullCoverage.delta >= 0 ? '+' : '\u2212'}${exact(Math.abs(data.fullCoverage.delta))} vs baseline`} />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th>Publisher</th>
                <th style={{ textAlign: 'right' }}>Requests</th>
                <th style={{ textAlign: 'right' }}>Bundles</th>
                <th style={{ textAlign: 'right' }}>Coverage</th>
                <th style={{ textAlign: 'right' }}>Missing</th>
              </tr>
            </thead>
            <tbody>
              {(pc.rows || []).length === 0 && <EmptyRow cols={5} text={`No publisher is below ${data.threshold}%.`} />}
              {(pc.rows || []).map(r => (
                <tr key={r.publisher}>
                  <td><span title={r.publisher} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.publisher}</span></td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} title={exact(r.requests)}>{nfmt(r.requests)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{exact(r.bundles)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: r.coveragePct === 0 ? 'var(--red)' : 'var(--amber)' }}>{r.coveragePct}%</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{exact(r.missingCount)} of {data.totalLines}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Honest accounting — never present a partial view as complete. */}
        <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 10, marginBottom: 0 }}>
          Based on {exact(ac.bundlesCovered)} of {exact(ac.bundlesScanned)} bundles · {exact(ac.excludedUnverifiable)} unverifiable · {exact(ac.excludedNoDomain)} with no developer domain.
        </p>
      </Panel>

      {/* ── Bundles carrying the full set ───────────────────────────────────── */}
      <Panel title={`Apps carrying every ${lineSetLabel === 'all lines' ? 'line' : lineSetLabel.replace(/s$/, '')}`}
             badge={<span className="badge badge-green">{exact(data.fullCoverage.total)}</span>}
             sub={`${exact(data.fullCoverage.total)} bundle${data.fullCoverage.total === 1 ? '' : 's'} carry all ${data.totalLines} \u2014 showing the top ${Math.min(20, data.fullCoverage.total)} by requests.`}
             right={<button className="btn btn-sm btn-secondary" disabled={!data.fullCoverage.total} onClick={() => copyToClipboard((data.fullCoverage.rows || []).map(r => r.bundle_id), 'bundle IDs')}><Copy size={13} /> Copy IDs</button>}>
        <MoverTable rows={data.fullCoverage.rows} cap={20} valueLabel="Requests" valueOf={r => r.inventory}
                    extra={r => <PublisherCell row={r} />} extraLabel="Publisher"
                    empty={data.totalLines === 0 ? 'No lines in this set \u2014 add some in Configuration & Settings.' : 'No bundle carries the whole set yet.'} />
      </Panel>

      {/* ── Discoveries ─────────────────────────────────────────────────────── */}
      <div className="grid-2" style={{ gap: 14 }}>
        <Panel title="New apps"
               badge={<span className="badge badge-cyan">{exact(data.newBundles.count)}</span>}
               sub={`${exact(data.newBundles.count)} first seen on ${p.dataDate} \u00b7 ${nfmt(data.newBundles.requests)} requests. Most read \"Not checked yet\" \u2014 an app is crawled on the run AFTER it first appears, so its authorization is unknown rather than absent.`}
               right={<button className="btn btn-sm btn-secondary" onClick={() => onGoTab && onGoTab('new')}><Sparkles size={13} /> Open</button>}>
          <MoverTable rows={data.newBundles.rows} cap={10} valueLabel="Requests" valueOf={r => r.inventory}
                      extra={r => <AuthCell row={r} totalLines={data.totalLines} setLabel={lineSetLabel} />}
                      extraLabel="Your lines in their file" empty="No new apps in this run." />
        </Panel>

        <Panel title="New publishers"
               badge={<span className="badge badge-cyan">{exact(data.newPublishers.count)}</span>}
               sub={`${exact(data.newPublishers.count)} developer domain${data.newPublishers.count === 1 ? '' : 's'} seen for the first time \u00b7 ${nfmt(data.newPublishers.requests)} requests. app-ads.txt lives on the domain, so one fix covers every bundle under it.`}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ fontSize: 12 }}>
              <thead><tr><th>Publisher</th><th style={{ textAlign: 'right' }}>Bundles</th><th style={{ textAlign: 'right' }}>Requests</th></tr></thead>
              <tbody>
                {(data.newPublishers.rows || []).length === 0 && <EmptyRow cols={3} text="No new publishers in this run." />}
                {(data.newPublishers.rows || []).map(r => (
                  <tr key={r.publisher}>
                    <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.publisher}</span></td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{exact(r.bundles)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} title={exact(r.requests)}>{nfmt(r.requests)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {/* ── Health ──────────────────────────────────────────────────────────── */}
      <Panel title="Verification health"
             badge={<>
               <span className="badge badge-amber">{exact(data.unverifiable.now)} unreadable</span>
               {data.newlyRemoved.count > 0 ? <span className="badge badge-red">{exact(data.newlyRemoved.count)} delisted</span> : null}
             </>}
             sub="How much of the picture Watch Tower could actually read. A rising unverifiable count means the coverage numbers above are built on less data, not that coverage got worse.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: (data.newlyRemoved.count > 0 ? 12 : 0) }}>
          <MiniStat label="Unverifiable bundles" value={exact(data.unverifiable.now)} color="var(--amber)"
                    sub={noBase ? 'file unreadable or no domain' : `${data.unverifiable.delta >= 0 ? '+' : '\u2212'}${exact(Math.abs(data.unverifiable.delta))} vs baseline`} />
          <MiniStat label="Newly removed from store" value={exact(data.newlyRemoved.count)} color="var(--red)" sub="app no longer live" />
          <MiniStat label="Top 20 concentration" value={cc.top20Now != null ? cc.top20Now + '%' : '\u2014'}
                    sub={cc.top20Prev != null ? `was ${cc.top20Prev}%` : 'share of all requests'} />
        </div>
        {data.newlyRemoved.count > 0 && (
          <MoverTable rows={data.newlyRemoved.rows} cap={10} valueLabel="Requests" valueOf={r => r.inventory}
                      extra={r => <PublisherCell row={r} />} extraLabel="Publisher" empty="None." />
        )}
      </Panel>
    </div>
  );
}

// A labelled filter control. Defined at MODULE scope, not inside the tab: a
// component declared in a render body gets a fresh identity every render, React
// treats that as a different component type and remounts it, and a <select> that
// remounts closes its own dropdown mid-use.
function FilterField({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: '1 1 165px', minWidth: 150 }}
           title={hint || ''}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
                     color: 'var(--text-muted)' }}>{label}</span>
      {children}
    </label>
  );
}

// ── Your Line Discovery ─────────────────────────────────────────────────────
// Every ads.txt line in the wild carrying one of the company's own domains,
// grouped by the publisher whose file it was found in. Deliberately shows the
// lines WITHOUT judgement — no "unrecognised seller ID" verdict, because that
// would flag the company's own new lines as suspicious every time one was added
// without a re-crawl.

const LD_FILES = [
  { v: 'app-ads.txt', label: 'app-ads.txt', hint: 'In-app supply. Populated by the nightly reconcile.' },
  { v: 'ads.txt',     label: 'ads.txt',     hint: 'Web supply. Only appears for web bundles.' },
];

function LineDiscoveryTab({ running }) {
  const [cfg, setCfg]         = useState(null);   // { domains, max, lastDiscovery }
  const [draft, setDraft]     = useState('');
  const [savingCfg, setSaving]= useState(false);
  const [file, setFile]       = useState('app-ads.txt');
  const [search, setSearch]   = useState('');
  const [domainF, setDomainF] = useState('');     // '' = every one of your domains
  const [relF, setRelF]       = useState('');     // '' = every relationship
  const [sjF, setSjF]         = useState('');     // '' = every sellers.json state
  const [statusF, setStatusF] = useState('');     // '' = active and dropped
  const [exporting, setExporting] = useState(false);
  // Held separately from `data` so a failed request does not empty the dropdowns
  // while a filter is still selected, which would render the select blank and
  // read as the filter having been lost rather than as the request having failed.
  const [facets, setFacets]   = useState({ domains: [], rels: [], sj: [], status: [] });
  const [sortCol, setSortCol] = useState('requests');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage]       = useState(1);
  const [data, setData]       = useState(null);
  const [openPub, setOpenPub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);

  const loadCfg = useCallback(async () => {
    try { const { data: d } = await axios.get(`${API}/own-domains`); setCfg(d); }
    catch (e) { setErr(errText(e, 'Could not load your domains')); }
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams({ file, search, sort: sortCol, dir: sortDir,
                                           page: String(page), pageSize: '25' });
      if (domainF) params.set('domain', domainF);
      if (relF)    params.set('relationship', relF);
      if (sjF)     params.set('sellersJson', sjF);
      if (statusF) params.set('status', statusF);
      const { data: d } = await axios.get(`${API}/line-discovery?${params}`);
      setData(d);
      setFacets({ domains: d.domainFacets || [], rels: d.relFacets || [],
                  sj: d.sjFacets || [], status: d.statusFacets || [] });
    } catch (e) { setErr(errText(e, 'Could not load discovered lines')); setData(null); }
    setLoading(false);
  }, [file, search, sortCol, sortDir, page, domainF, relF, sjF, statusF]);
  useEffect(() => { load(); }, [load]);

  // A finished run changes what has been discovered.
  const wasRunning = useRef(running);
  useEffect(() => {
    if (wasRunning.current && !running) { load(); loadCfg(); }
    wasRunning.current = running;
  }, [running, load, loadCfg]);

  const domains = (cfg && cfg.domains) || [];
  const max = (cfg && cfg.max) || 5;

  const saveDomains = async (next) => {
    setSaving(true);
    try {
      const { data: d } = await axios.put(`${API}/own-domains`, { domains: next });
      if (d.rejected && d.rejected.length) toast.error(`Not a valid domain: ${d.rejected.join(', ')}`);
      else toast.success('Domains saved');
      setDraft('');
      await loadCfg();
      await load();
    } catch (e) { toast.error(errText(e, 'Could not save')); }
    setSaving(false);
  };
  const addDomain = () => {
    const v = draft.trim();
    if (!v) return;
    if (domains.length >= max) { toast.error(`At most ${max} domains.`); return; }
    saveDomains([...domains.map(d => d.domain), v]);
  };
  const removeDomain = (d) => saveDomains(domains.map(x => x.domain).filter(x => x !== d));

  const sortBy = (col) => {
    if (sortCol === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
    setPage(1);
  };

  const domainOpts = facets.domains;
  const relOpts    = facets.rels;
  const sjOpts     = facets.sj;
  const statusOpts = facets.status;
  const filtered   = !!(domainF || relF || sjF || statusF || search);
  // A value that is blank once trimmed would be an invisible row in the
  // dropdown. Label it, but send the raw value back so it stays selectable.
  const relLabel = (v) => (String(v || '').trim() ? v : '(blank)');
  // Worded exactly as the badges in the table below, so the filter and the
  // result read as the same vocabulary.
  const SJ_LABEL = { listed: 'Listed', not_listed: 'Not listed',
                     no_seller_id: 'No seller ID', no_data: 'No data' };
  const ST_LABEL = { active: 'Active', dropped: 'Dropped' };

  const clearFilters = () => {
    setDomainF(''); setRelF(''); setSjF(''); setStatusF(''); setSearch(''); setPage(1);
  };

  // The whole filtered set, not the page -- the server streams it. Through axios
  // with responseType 'blob', never window.open: window.open is a plain browser
  // navigation and carries no Authorization header, which is exactly what made
  // the Line Checker CSV come back "Authorization required".
  const exportCsv = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams({ file, search, format: 'csv' });
      if (domainF) p.set('domain', domainF);
      if (relF)    p.set('relationship', relF);
      if (sjF)     p.set('sellersJson', sjF);
      if (statusF) p.set('status', statusF);
      const res = await axios.get(`${API}/line-discovery?${p}`, { responseType: 'blob' });
      const bits = ['line-discovery', file.replace(/\./g, '-'), domainF || null,
                    relF ? String(relF).toLowerCase().replace(/[()]/g, '') : null,
                    sjF ? sjF.replace(/_/g, '-') : null, statusF || null]
        .filter(Boolean).join('-').replace(/[^a-z0-9.-]+/gi, '-');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      a.download = `${bits}.csv`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch (e) {
      // With responseType 'blob' an error body arrives as a Blob, so the usual
      // error reader finds no .error on it and falls back to the bare status.
      // Read the blob to recover the message the server actually sent.
      let msg = null;
      const body = e && e.response && e.response.data;
      if (body instanceof Blob) {
        try { msg = (JSON.parse(await body.text()) || {}).error; } catch { /* not JSON */ }
      }
      toast.error(msg || errText(e, 'Could not export the CSV'));
    }
    setExporting(false);
  };

  // ── domain configuration ──────────────────────────────────────────────────
  const config = (
    <div className="card" style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>Your domains</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
        The domains publishers write in the first field when they declare you, up to {max}.
        Lines are collected during the nightly crawl, so a domain added now stays empty until the next run.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {domains.map(d => (
          <span key={d.domain} className="badge badge-muted"
                style={{ fontFamily: 'var(--font-mono)', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {d.domain}
            <button onClick={() => removeDomain(d.domain)} disabled={savingCfg}
                    title="Stop collecting for this domain (already-found lines are kept)"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 0, display: 'flex' }}>
              <X size={11} />
            </button>
          </span>
        ))}
        {domains.length < max && (
          <>
            <input className="input-field" value={draft} onChange={e => setDraft(e.target.value)}
                   onKeyDown={e => { if (e.key === 'Enter') addDomain(); }}
                   placeholder="yourdomain.com" style={{ width: 200, fontSize: 12 }} />
            <button className="btn btn-sm btn-secondary" onClick={addDomain} disabled={savingCfg || !draft.trim()}>
              <Plus size={13} /> Add
            </button>
          </>
        )}
      </div>
      {/* Removing a domain stops collection but keeps what was found — deleting it
          would destroy evidence that may be mid-conversation with a partner. */}
      {cfg && cfg.lastDiscovery && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
          Last collected {cfg.lastDiscovery}.
        </div>
      )}
    </div>
  );

  if (!cfg) return <div className="card" style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)', fontSize: 13 }}><span className="spinner" /> Loading…</div>;

  if (!domains.length) return (
    <div>
      {config}
      <div className="card" style={{ textAlign: 'center', padding: 34 }}>
        <Search size={26} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Add a domain to start</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 5, maxWidth: 520, margin: '5px auto 0' }}>
          This finds every ads.txt and app-ads.txt line in the wild that carries one of your domains —
          including seller IDs you never configured. Nothing is collected until at least one domain is set.
        </div>
      </div>
    </div>
  );

  const sum = (data && data.summary) || {};
  const rows = (data && data.rows) || [];
  const fileMeta = LD_FILES.find(f => f.v === file);

  return (
    <div>
      {config}

      <div className="tabs" style={{ marginBottom: 12 }}>
        {LD_FILES.map(f => (
          <button key={f.v} className={`tab ${file === f.v ? 'active' : ''}`}
                  onClick={() => { setFile(f.v); setPage(1); setOpenPub(null);
                                   // The two files have different publishers, so a
                                   // filter set on one can be absent from the other,
                                   // and leaving it on would show an empty table that
                                   // reads as broken rather than as filtered.
                                   setDomainF(''); setRelF(''); setSjF(''); setStatusF(''); }}>{f.label}</button>
        ))}
      </div>

      {/* Filters on their own labelled row. Every count comes from the server and
          is computed WITHOUT the current filters, so choosing one never makes the
          others vanish and leaves you on a dead end. */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
        <FilterField label="Your domains"
                     hint="Which of your domains the publisher wrote in the first field of the line">
          <select className="select-field" value={domainF} style={{ fontSize: 12 }}
                  onChange={e => { setDomainF(e.target.value); setPage(1); setOpenPub(null); }}>
            <option value="">All ({exact(domainOpts.reduce((t, o) => t + o.count, 0))})</option>
            {domainOpts.map(o => (
              <option key={o.value} value={o.value}>{o.value} ({exact(o.count)})</option>
            ))}
          </select>
        </FilterField>

        <FilterField label="Relationship"
                     hint="The third field of the line, exactly as the publisher wrote it">
          <select className="select-field" value={relF} style={{ fontSize: 12 }}
                  onChange={e => { setRelF(e.target.value); setPage(1); setOpenPub(null); }}>
            <option value="">All ({exact(relOpts.reduce((t, o) => t + o.count, 0))})</option>
            {relOpts.map(o => (
              <option key={o.value} value={o.value}>{relLabel(o.value)} ({exact(o.count)})</option>
            ))}
          </select>
        </FilterField>

        <FilterField label="In your sellers.json"
                     hint="Whether the seller ID on the line is declared in your own sellers.json. 'No data' means your file could not be read, so nothing can be claimed either way.">
          <select className="select-field" value={sjF} style={{ fontSize: 12 }}
                  onChange={e => { setSjF(e.target.value); setPage(1); setOpenPub(null); }}>
            <option value="">All ({exact(sjOpts.reduce((t, o) => t + o.count, 0))})</option>
            {sjOpts.map(o => (
              <option key={o.value} value={o.value}>{SJ_LABEL[o.value] || o.value} ({exact(o.count)})</option>
            ))}
          </select>
        </FilterField>

        {/* One "Dropped" option, as asked. The per-line date stays on the badge in
            the table, where it is actionable; as a filter value it would be
            useless, since every dropped line has a different one. */}
        <FilterField label="Status"
                     hint="Active = present on the latest crawl. Dropped = last seen earlier; the table shows each line's own last-seen date.">
          <select className="select-field" value={statusF} style={{ fontSize: 12 }}
                  onChange={e => { setStatusF(e.target.value); setPage(1); setOpenPub(null); }}>
            <option value="">All ({exact(statusOpts.reduce((t, o) => t + o.count, 0))})</option>
            {statusOpts.map(o => (
              <option key={o.value} value={o.value}>{ST_LABEL[o.value] || o.value} ({exact(o.count)})</option>
            ))}
          </select>
        </FilterField>

        {filtered && (
          <button className="btn btn-sm btn-secondary" onClick={clearFilters}
                  title="Clear all four filters and the search" style={{ marginBottom: 1 }}>
            <X size={13} /> Clear
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input className="input-field" value={search}
               onChange={e => { setSearch(e.target.value); setPage(1); }}
               placeholder="Search publisher, seller ID, or line text…" style={{ flex: 1, minWidth: 200, fontSize: 12 }} />
        <button className="btn btn-sm btn-secondary" onClick={load} disabled={loading}>
          {loading ? <span className="spinner" /> : <RefreshCw size={14} />} Refresh
        </button>
        <button className="btn btn-sm btn-secondary" disabled={!rows.length}
                onClick={() => copyToClipboard(rows.flatMap(r => (r.lines || []).map(l => l.raw_line)), 'lines')}>
          <Copy size={13} /> Copy lines
        </button>
        {/* Exports everything the filters match, not the 25 publishers on screen. */}
        <button className="btn btn-sm btn-primary" onClick={exportCsv} disabled={exporting || !rows.length}
                title={sum.lines
                  ? `Download all ${exact(sum.lines)} matching line${sum.lines === 1 ? '' : 's'} across ${exact(sum.publishers)} publisher${sum.publishers === 1 ? '' : 's'} — not just this page`
                  : 'Download the lines these filters match'}>
          {exporting ? <span className="spinner" /> : <Download size={13} />} Export CSV
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
        <MiniStat label={filtered ? 'Publishers matching these filters' : 'Publishers carrying your lines'}
                  value={exact(sum.publishers)} />
        <MiniStat label={filtered ? `Matching lines in ${file}` : `Lines found in ${file}`}
                  value={exact(sum.lines)} color="var(--accent)" />
        <MiniStat label="Requests from these publishers" value={nfmt(sum.requests)} />
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 0, marginBottom: 10 }}>
        {fileMeta ? fileMeta.hint : ''} Lines are shown exactly as they appear in the publisher&rsquo;s file —
        including seller IDs you did not configure. A line that a publisher removes keeps its last-seen date rather than disappearing.
      </p>

      {err && (
        <div className="card" style={{ borderColor: 'var(--red)', padding: '10px 12px', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertTriangle size={14} style={{ color: 'var(--red)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{err}</div>
        </div>
      )}

      <div className="card">
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => sortBy('publisher')}>Publisher {sortCol === 'publisher' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => sortBy('lines')}>Lines {sortCol === 'lines' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => sortBy('requests')}>Requests {sortCol === 'requests' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                <th style={{ textAlign: 'right' }}>Apps</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {loading && !rows.length && <EmptyRow cols={5} text="Loading…" />}
              {!loading && !rows.length && (
                <EmptyRow cols={5} text={filtered
                  ? 'No lines match these filters.'
                  : `No ${file} lines found yet — they are collected during the nightly crawl.`} />
              )}
              {rows.map(r => {
                const on = openPub === r.publisher;
                return (
                  <React.Fragment key={r.publisher}>
                    <tr onClick={() => setOpenPub(on ? null : r.publisher)} style={{ cursor: 'pointer' }}
                        title="Show the lines found on this publisher">
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <ChevronRight size={12} style={{ color: 'var(--text-muted)', transform: on ? 'rotate(90deg)' : 'none', transition: 'transform 120ms ease' }} />
                          <span style={{ fontFamily: 'var(--font-mono)' }}>{r.publisher}</span>
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{exact(r.lineCount)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} title={exact(r.requests)}>{nfmt(r.requests)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{exact(r.bundles)}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{r.lastSeen}</td>
                    </tr>
                    {on && (
                      <tr>
                        <td colSpan={5} style={{ background: 'var(--bg-surface)', padding: '10px 12px' }}>
                          <div style={{ overflowX: 'auto' }}>
                            <table className="data-table" style={{ fontSize: 11 }}>
                              <thead>
                                <tr>
                                  <th>Seller ID</th>
                                  <th>Relationship</th>
                                  <th>Line as published</th>
                                  <th>In your sellers.json</th>
                                  <th>Seller name</th>
                                  <th>Seller domain</th>
                                  <th>Type</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(r.lines || []).map(l => (
                                  <tr key={l.raw_line}>
                                    <td style={{ fontFamily: 'var(--font-mono)' }}>{l.seller_id || '—'}</td>
                                    <td>
                                      {l.relationship
                                        ? <span className={`badge ${l.relationship === 'DIRECT' ? 'badge-green' : 'badge-muted'}`}>{l.relationship}</span>
                                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                                    </td>
                                    {/* Raw, verbatim: the formatting is often the interesting part. */}
                                    <td style={{ fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxWidth: 460 }}>{l.raw_line}</td>
                                    {/* Three states. 'no_data' means this domain has not been
                                        ingested into the Sellers.json Database, so we cannot say
                                        anything — showing "not listed" there would be a false claim. */}
                                    <td>
                                      {l.seller_json_status === 'listed'
                                        ? <span className="badge badge-green" title="This seller ID is present in your sellers.json">Listed</span>
                                        : l.seller_json_status === 'not_listed'
                                          ? <span className="badge badge-red" title="This publisher declares a seller ID that is NOT in your sellers.json — a stale ID, a typo, or someone using your domain">Not listed</span>
                                          : l.seller_json_status === 'no_seller_id'
                                            ? <span className="badge badge-muted" title="The line carries no seller ID to check">—</span>
                                            : <span className="badge badge-muted"
                                                    title={l.seller_json_note
                                                      ? `Your sellers.json could not be read: ${l.seller_json_note}`
                                                      : 'Your sellers.json could not be read, so there is nothing to compare against'}>No data</span>}
                                    </td>
                                    {/* From YOUR sellers.json, only meaningful when the ID matched.
                                        Confidential sellers legitimately omit name and domain. */}
                                    <td style={{ fontSize: 11, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        title={l.seller_name || ''}>
                                      {l.seller_json_status !== 'listed'
                                        ? <span style={{ color: 'var(--text-muted)' }}>—</span>
                                        : l.seller_name
                                          ? l.seller_name
                                          : l.seller_confidential
                                            ? <span className="badge badge-muted" style={{ fontSize: 10 }} title="Declared confidential in your sellers.json, so name and domain are omitted by design">Confidential</span>
                                            : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                                    </td>
                                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                                      {l.seller_json_status === 'listed' && l.seller_domain
                                        ? l.seller_domain
                                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                                    </td>
                                    <td>
                                      {l.seller_json_status !== 'listed' || !l.seller_type
                                        ? <span style={{ color: 'var(--text-muted)' }}>—</span>
                                        : <span className={`badge ${l.seller_type === 'PUBLISHER' ? 'badge-green' : l.seller_type === 'INTERMEDIARY' ? 'badge-amber' : 'badge-muted'}`}
                                                style={{ fontSize: 10 }}>{l.seller_type}</span>}
                                    </td>
                                    {/* Active vs dropped, rather than a bare date nobody can interpret. */}
                                    <td style={{ color: 'var(--text-secondary)' }}>
                                      {data.latestSeen && l.last_seen === data.latestSeen
                                        ? <span className="badge badge-green" title={`Present on the latest crawl (${l.last_seen})`}>Active</span>
                                        : <span className="badge badge-amber" title={`Last present on ${l.last_seen}; absent from the crawl on ${data.latestSeen || 'a later date'}`}>
                                            Dropped {l.last_seen}
                                          </span>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pager meta={data} onPage={setPage} />
      </div>
    </div>
  );
}

// ── Exposure tab ───────────────────────────────────────────────────────────────────
function ExposureTab({ lines }) {
  const [sel, setSel] = useState([]);
  const [lineKind, setLineKind] = useState('all');
  const [search, setSearch] = useState('');
  const [env, setEnv] = useState('all');
  const [stateF, setStateF] = useState('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const recheckPoll = useRef(null);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const [sortCol, setSortCol] = useState('inventory');
  const [sortDir, setSortDir] = useState('desc');
  function handleSort(col) { setPage(1); if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); else { setSortCol(col); setSortDir('asc'); } }
  const [diagSortCol, setDiagSortCol] = useState('inventory');
  const [diagSortDir, setDiagSortDir] = useState('desc');
  function handleDiagSort(col) { if (diagSortCol === col) setDiagSortDir(d => (d === 'asc' ? 'desc' : 'asc')); else { setDiagSortCol(col); setDiagSortDir('asc'); } }

  const load = useCallback(async () => {
    if (!sel.length) { setData(null); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ lines: sel.join(','), page: String(page), pageSize: String(PAGE_SIZE), search, env, state: stateF, sort: sortCol, dir: sortDir });
      const { data } = await axios.get(`${API}/results?${params}`);
      setData(data);
    } catch (e) { toast.error('Could not load results'); }
    setLoading(false);
  }, [sel, search, env, stateF, page, sortCol, sortDir]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (recheckPoll.current) clearInterval(recheckPoll.current); }, []);

  const toggle = (id) => {
    setPage(1);
    setSel(prev => prev.includes(id) ? prev.filter(x => x !== id) : (prev.length >= MAX_COLS ? prev : [...prev, id]));
  };
  const capped = sel.length >= MAX_COLS;

  const patchRow = (u) => {
    setData(prev => {
      if (!prev) return prev;
      const state = u.verify_state === 'removed' ? 'removed' : u.verify_state === 'unverifiable' ? 'unver' : (sel.some(id => (u.line_presence || {})[id]) ? 'auth' : 'unauth');
      return { ...prev, rows: prev.rows.map(r => r.bundle_id === u.bundle_id ? { ...r, ...u, state } : r) };
    });
  };
  const recheckRow = async (id) => {
    try {
      const { data: d } = await axios.post(`${API}/recheck`, { ids: [id] });
      (d.rows || []).forEach(patchRow);
      toast.success('Re-checked');
    } catch (e) { toast.error('Re-check failed'); }
  };
  const recheckAll = async () => {
    try {
      const { data: d } = await axios.post(`${API}/recheck`, {});
      if (d.status !== 'running') { toast('Nothing to re-check'); return; }
      setRechecking(true);
      if (recheckPoll.current) clearInterval(recheckPoll.current);
      let tries = 0;
      recheckPoll.current = setInterval(async () => {
        tries++;
        try {
          const { data: st } = await axios.get(`${API}/run-status`);
          if (st.status === 'done') { clearInterval(recheckPoll.current); recheckPoll.current = null; setRechecking(false); toast.success('Re-check complete'); load(); }
          else if (st.status === 'error') { clearInterval(recheckPoll.current); recheckPoll.current = null; setRechecking(false); toast.error(`Re-check failed: ${st.error || ''}`); }
          else if (tries > 300) { clearInterval(recheckPoll.current); recheckPoll.current = null; setRechecking(false); toast('Still running — refresh shortly to see results.'); }
        } catch (e) { if (tries > 300) { clearInterval(recheckPoll.current); recheckPoll.current = null; setRechecking(false); } }
      }, 3000);
    } catch (e) { toast.error('Re-check failed'); }
  };

  const runDiagnostic = async () => {
    setDiagLoading(true); setDiag(null);
    try { const { data: d } = await axios.get(`${API}/diagnose-unresolved`); setDiag(d); }
    catch (e) { toast.error('Diagnostic failed'); }
    setDiagLoading(false);
  };

  const visLines = lines.filter(l => lineKind === 'all' || lineKindOf(l) === lineKind);
  const selLineObjs = sel.map(id => lines.find(l => String(l.id) === String(id))).filter(Boolean);
  const s = data?.summary;

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            Select lines to compare <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>— up to {MAX_COLS}, each becomes a column</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select className="select-field" value={lineKind} onChange={e => setLineKind(e.target.value)} style={{ width: 150, fontSize: 12 }}>
              {LINE_KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
            </select>
            <span className="badge badge-cyan" style={{ whiteSpace: 'nowrap' }}>{sel.length} / {MAX_COLS} selected</span>
            {sel.length > 0 && <button className="btn btn-sm btn-secondary" onClick={() => { setSel([]); setPage(1); }}>Clear</button>}
          </div>
        </div>
        <div style={{ maxHeight: 168, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-surface)', padding: 12 }}>
          {visLines.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No lines of this type — add some in Configuration &amp; Settings.</span>}
          {['demand', 'yours'].map(kind => {
            const group = visLines.filter(l => lineKindOf(l) === kind);
            if (!group.length) return null;
            return (
              <div key={kind} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', fontWeight: 700, marginBottom: 7 }}>
                  {kind === 'demand' ? 'Demand lines' : 'Your lines'} · {group.length}
                </div>
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                  {group.map(l => {
                    const on = sel.includes(String(l.id));
                    const disabled = !on && capped;
                    return (
                      <button key={l.id} onClick={() => toggle(String(l.id))} disabled={disabled} title={l.line}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999,
                          fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                          border: on ? '1px solid var(--accent)' : '1px solid var(--border-bright)',
                          background: on ? 'var(--accent-dim)' : 'var(--bg-elevated)',
                          color: on ? 'var(--accent)' : 'var(--text-secondary)',
                          cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.35 : 1,
                          transition: 'all .12s ease',
                        }}>
                        {on ? <Check size={13} /> : <Plus size={13} />} {l.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {capped && <div style={{ fontSize: 11, color: 'var(--amber)', marginTop: 6 }}>Maximum {MAX_COLS} columns selected — deselect one to choose another.</div>}
      </div>

      {s && (
        <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>Last fetch{s.lastRun ? ` \u00b7 ${s.lastRun}` : ''}</div>
            <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>
              {Number(s.fetchedLastRun || 0).toLocaleString()} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)' }}>bundles fetched</span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>Newly identified</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--green)', fontVariantNumeric: 'tabular-nums' }}>+{Number(s.newLastRun || 0).toLocaleString()}</div>
          </div>
        </div>
      )}
      {sel.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 14 }}>
          <MiniStat label="Fetched & checked" value={s ? s.checked.toLocaleString() : '—'} />
          <MiniStat label="Authorized" value={s ? s.authorized.toLocaleString() : '—'} color="var(--green)" />
          <MiniStat label="Unauthorized · receiving" value={s ? s.unauthorized.toLocaleString() : '—'} color="var(--red)" sub={s ? `${Number(s.exposedInventory).toLocaleString()} requests at risk` : ''} />
          <MiniStat label="Unverifiable" value={s ? s.unverifiable.toLocaleString() : '—'} color="var(--amber)" />
          <MiniStat label="Removed from store" value={s ? (s.removed || 0).toLocaleString() : '—'} color="var(--red)" sub="app no longer live" />
        </div>
      )}

      {stateF === 'unver' && s && s.unverifiable > 0 && Array.isArray(s.unverifiableReasons) && s.unverifiableReasons.length > 0 && (
        <div style={{ marginBottom: 14, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-surface)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--text-primary)', marginBottom: 8, fontWeight: 600 }}>Why these are unverifiable</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {s.unverifiableReasons.map(({ reason, count }) => (
              <span key={reason} className="badge badge-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                {reason} <strong style={{ color: 'var(--amber)' }}>{Number(count).toLocaleString()}</strong>
              </span>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 9, lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--text-primary)' }}>Re-check all unverifiable</strong> re-crawls only the bundles that already have a developer domain (connection / site failures — usually recoverable). Rows with <em>no developer domain</em> have no app-ads.txt location to check and re-resolve on the daily run, not on re-check.
          </div>
        </div>
      )}

      {stateF === 'unver' && (
        <div style={{ marginBottom: 14, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-surface)', padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 600 }}>Storefront diagnostic</div>
            <button className="btn btn-sm btn-secondary" onClick={runDiagnostic} disabled={diagLoading}>
              {diagLoading ? <span className="spinner" /> : <Search size={13} />} Sample top 25 across all storefronts
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>
            Checks a live sample of your highest-request “no developer domain” bundles against all 14 Apple / Google storefronts — to show whether they're recoverable and which geos would resolve them, before running a full resolution pass.
          </div>

          {diag && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 10 }}>
                <MiniStat label="Unresolved app bundles" value={Number(diag.total).toLocaleString()} />
                <MiniStat label="iOS / Android" value={`${Number(diag.ios).toLocaleString()} / ${Number(diag.android).toLocaleString()}`} />
                <MiniStat label="Requests at stake" value={Number(diag.inventory).toLocaleString()} />
              </div>

              <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, marginBottom: 6 }}>Sample of {diag.sample.size} highest-inventory bundles</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                <span className="badge badge-green" style={{ display: 'inline-flex', gap: 5 }}>Recoverable <strong>{diag.sample.recovered}</strong></span>
                <span className="badge badge-amber" style={{ display: 'inline-flex', gap: 5 }}>Live, no website <strong>{diag.sample.presentNoWebsite}</strong></span>
                <span className="badge badge-red" style={{ display: 'inline-flex', gap: 5 }}>Removed <strong>{diag.sample.gone}</strong></span>
                <span className="badge badge-muted" style={{ display: 'inline-flex', gap: 5 }}>Inconclusive <strong>{diag.sample.inconclusive}</strong></span>
              </div>

              {diag.sample.geoBreakdown.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, marginBottom: 6 }}>Recovered from storefronts — this is the geo signal</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {diag.sample.geoBreakdown.map(({ geo, count }) => (
                      <span key={geo} className="badge badge-cyan" style={{ display: 'inline-flex', gap: 5 }}>{geoLabel(geo)} <strong>{count}</strong></span>
                    ))}
                  </div>
                </div>
              )}

              <div className="card" style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ fontSize: 12 }}>
                  <thead><tr>
                    <SortHeader col="bundle_id" label="Bundle" current={diagSortCol} dir={diagSortDir} onSort={handleDiagSort} />
                    <SortHeader col="platform" label="Platform" current={diagSortCol} dir={diagSortDir} onSort={handleDiagSort} />
                    <SortHeader col="inventory" label="Requests" current={diagSortCol} dir={diagSortDir} onSort={handleDiagSort} style={{ textAlign: 'right' }} />
                    <SortHeader col="outcome" label="Outcome" current={diagSortCol} dir={diagSortDir} onSort={handleDiagSort} />
                    <SortHeader col="geo" label="Found in" current={diagSortCol} dir={diagSortDir} onSort={handleDiagSort} />
                    <SortHeader col="domain" label="Domain" current={diagSortCol} dir={diagSortDir} onSort={handleDiagSort} />
                  </tr></thead>
                  <tbody>
                    {sortRows(diag.sample.rows, diagSortCol, diagSortDir).map(r => (
                      <tr key={r.bundle_id}>
                        <td><div style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{r.bundle_id}</div><div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{r.app_name || ''}</div></td>
                        <td>{r.platform === 'ios' ? 'iOS' : 'Android'}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(r.inventory).toLocaleString()}</td>
                        <td>{r.outcome === 'recovered' ? <span style={{ color: 'var(--green)' }}>Recoverable</span>
                           : r.outcome === 'present-no-website' ? <span style={{ color: 'var(--amber)' }}>No website</span>
                           : r.outcome === 'gone' ? <span style={{ color: 'var(--red)' }}>Removed</span>
                           : <span style={{ color: 'var(--text-secondary)' }}>Inconclusive</span>}</td>
                        <td>{r.geo ? geoLabel(r.geo) : '—'}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{r.domain || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <input className="input-field" placeholder="Search name / bundle / domain…" value={search} onChange={e => { setPage(1); setSearch(e.target.value); }} style={{ flex: 1, minWidth: 160 }} />
        <select className="select-field" value={env} onChange={e => { setPage(1); setEnv(e.target.value); }} style={{ width: 120 }}>
          <option value="all">All env</option><option value="app">App</option><option value="web">Web</option>
        </select>
        <select className="select-field" value={stateF} onChange={e => { setPage(1); setStateF(e.target.value); }} style={{ width: 170 }}>
          <option value="all">All states</option><option value="auth">Has authorized line</option><option value="unauth">No line found</option><option value="unver">Unverifiable</option><option value="removed">Removed from store</option>
        </select>
        <button className="btn btn-sm btn-secondary" onClick={recheckAll} disabled={rechecking}>{rechecking ? <span className="spinner" /> : <RefreshCw size={14} />} Re-check all unverifiable</button>
        <CopyMenu disabled={!data?.rows?.length} onPick={async (kind) => {
          if (stateF === 'all') { toast.error('Pick a status (Authorized / Unauthorized / Unverifiable / Removed) first \u2014 "All states" could be 150k+ bundles.'); return; }
          const params = new URLSearchParams({ lines: sel.join(','), search, env, state: stateF, export: kind });
          const { data: ex } = await axios.get(`${API}/results?${params}`);
          copyToClipboard(ex.values, kind === 'ids' ? 'IDs' : 'domains');
        }} />
      </div>

      {sel.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><AlertTriangle size={28} /></div>
          <div className="empty-state-text">Select up to {MAX_COLS} lines above</div>
          <div className="empty-state-sub">Each selected line becomes a column showing authorization across your fetched bundles.</div>
        </div>
      ) : loading ? (
        <div className="empty-state"><span className="spinner" /></div>
      ) : (
        <>
          <div className="card" style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 560 + sel.length * 110 }}>
              <thead>
                <tr>
                  <SortHeader col="developer_domain" label="Publisher" current={sortCol} dir={sortDir} onSort={handleSort} />
                  <SortHeader col="bundle_id" label="Bundle / domain" current={sortCol} dir={sortDir} onSort={handleSort} />
                  <SortHeader col="env" label="Env" current={sortCol} dir={sortDir} onSort={handleSort} />
                  <SortHeader col="inventory" label="Requests" current={sortCol} dir={sortDir} onSort={handleSort} style={{ textAlign: 'right' }} />
                  {selLineObjs.map(l => <SortHeader key={l.id} col={`line:${l.id}`} label={l.name} current={sortCol} dir={sortDir} onSort={handleSort} style={{ textAlign: 'center' }} />)}
                  <th>ads.txt</th>
                  <th>Privacy Policy</th>
                  <SortHeader col="store_url" label="Store" current={sortCol} dir={sortDir} onSort={handleSort} />
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).map(r => (
                  <tr key={r.bundle_id}>
                    <td><PublisherCell row={r} /></td>
                    <td>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.bundle_id}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.verify_reason || r.app_name || ''}</div>
                    </td>
                    <td><EnvBadge env={r.env} /></td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(r.inventory || 0).toLocaleString()}</td>
                    {sel.map(id => {
                      const cell = r.verify_state === 'removed' ? 'removed' : r.verify_state === 'unverifiable' ? 'unver' : ((r.line_presence || {})[id] ? 'auth' : 'unauth');
                      return <td key={id} style={{ textAlign: 'center' }}><StateDot state={cell} /></td>;
                    })}
                    <td><AdsTxtLink row={r} /></td>
                    <td><PrivacyLink row={r} /></td>
                    <td><StoreLink row={r} /></td>
                    <td>{(r.verify_state === 'unverifiable' || r.verify_state === 'removed')
                      ? <button className="btn btn-sm btn-secondary" onClick={() => recheckRow(r.bundle_id)}><RefreshCw size={12} /> Re-check</button>
                      : <span style={{ color: 'var(--text-secondary)' }}>—</span>}</td>
                  </tr>
                ))}
                {(!data?.rows || data.rows.length === 0) && <tr><td colSpan={8 + sel.length} style={{ color: 'var(--text-secondary)', padding: 14 }}>No bundles match.</td></tr>}
              </tbody>
            </table>
          </div>
          <Pager meta={data} onPage={setPage} />
        </>
      )}
    </div>
  );
}

// ── Missed Coverage tab ───────────────────────────────────────────────────────
function MissedTab({ lines }) {
  const [lineKind, setLineKind] = useState('all');
  const [line, setLine] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);

  // Demand lines show only once linked to a partner's list(s); Your lines always show.
  const isLinked = l => !!(l.linked_allow_list_id || l.linked_block_list_id);
  const visLines = lines.filter(l => {
    if (lineKindOf(l) === 'yours') return lineKind === 'all' || lineKind === 'yours';
    return (lineKind === 'all' || lineKind === 'demand') && isLinked(l);
  });
  useEffect(() => {
    if (!visLines.find(l => String(l.id) === String(line))) setLine(visLines.length ? String(visLines[0].id) : '');
  }, [lineKind, lines]);

  const [sortCol, setSortCol] = useState('inventory');
  const [sortDir, setSortDir] = useState('desc');
  function handleSort(col) { setPage(1); if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); else { setSortCol(col); setSortDir('asc'); } }

  const load = useCallback(async () => {
    if (!line) { setData(null); return; }
    try {
      const params = new URLSearchParams({ line, page: String(page), pageSize: String(PAGE_SIZE), search, sort: sortCol, dir: sortDir });
      const { data } = await axios.get(`${API}/missed?${params}`);
      setData(data);
    } catch (e) { toast.error('Could not load'); }
  }, [line, search, page, sortCol, sortDir]);
  useEffect(() => { load(); }, [load]);

  const lineName = lines.find(l => String(l.id) === String(line))?.name || '';

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <select className="select-field" value={lineKind} onChange={e => setLineKind(e.target.value)} style={{ width: 150 }}>
          {LINE_KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
        </select>
        <select className="select-field" value={line} onChange={e => { setPage(1); setLine(e.target.value); }} style={{ minWidth: 180 }}>
          {visLines.length === 0 && <option value="">No lines of this type</option>}
          {visLines.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <input className="input-field" placeholder="Search…" value={search} onChange={e => { setPage(1); setSearch(e.target.value); }} style={{ flex: 1, minWidth: 140 }} />
        <CopyMenu disabled={!data?.rows?.length} onPick={async (kind) => {
          const params = new URLSearchParams({ line, search, export: kind });
          const { data: ex } = await axios.get(`${API}/missed?${params}`);
          copyToClipboard(ex.values, kind === 'ids' ? 'IDs' : 'domains');
        }} />
      </div>
      {data?.summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
          <MiniStat label="Submit candidates" value={Number(data.summary.candidates || 0).toLocaleString()} color="var(--green)" />
          <MiniStat label="Requests in play" value={Number(data.summary.inventory || 0).toLocaleString()} sub="authorized but unlisted" />
          <MiniStat label="Via line" value={lineName || '—'} />
        </div>
      )}
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
        {data?.mode === 'yours'
          ? <>Bundles where your line <strong style={{ color: 'var(--text-primary)' }}>{lineName}</strong> is present — authorized requests.</>
          : <>Receiving requests via <strong style={{ color: 'var(--text-primary)' }}>{lineName}</strong>, authorized, and not in this partner&apos;s linked lists yet — candidates to submit for approval.</>}
      </p>
      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead><tr>
            <SortHeader col="developer_domain" label="Publisher" current={sortCol} dir={sortDir} onSort={handleSort} />
            <SortHeader col="bundle_id" label="Bundle / domain" current={sortCol} dir={sortDir} onSort={handleSort} />
            <SortHeader col="env" label="Env" current={sortCol} dir={sortDir} onSort={handleSort} />
            <SortHeader col="store_url" label="Store" current={sortCol} dir={sortDir} onSort={handleSort} />
            <th>ads.txt</th>
            <th>Privacy Policy</th>
            <SortHeader col="inventory" label="Requests" current={sortCol} dir={sortDir} onSort={handleSort} style={{ textAlign: 'right' }} />
            <th>List status</th><th>Can submit</th>
          </tr></thead>
          <tbody>
            {(data?.rows || []).map(r => (
              <tr key={r.bundle_id}>
                <td><PublisherCell row={r} /></td>
                <td><div style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.bundle_id}</div><div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.app_name || ''}</div></td>
                <td><EnvBadge env={r.env} /></td>
                <td><StoreLink row={r} /></td>
                <td><AdsTxtLink row={r} /></td>
                <td><PrivacyLink row={r} /></td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(r.inventory || 0).toLocaleString()}</td>
                <td><span className="badge badge-muted">{data?.mode === 'yours' ? 'Authorized' : 'Not in linked lists'}</span></td>
                <td><span className="badge badge-green"><Check size={12} /> Yes</span></td>
              </tr>
            ))}
            {(!data?.rows || data.rows.length === 0) && <tr><td colSpan={9} style={{ color: 'var(--text-secondary)', padding: 14 }}>No candidates for this line.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager meta={data} onPage={setPage} />
    </div>
  );
}

// ── New Today tab ───────────────────────────────────────────────────────────────
function NewTodayTab({ lines }) {
  const [lineKind, setLineKind] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const lineById = (id) => lines.find(l => String(l.id) === String(id));
  const nameOf = (id) => lineById(id)?.name || id;
  const [line, setLine] = useState('');
  const linkedDemand = lines.filter(l => lineKindOf(l) === 'demand' && (l.linked_allow_list_id || l.linked_block_list_id));

  const [sortCol, setSortCol] = useState('inventory');
  const [sortDir, setSortDir] = useState('desc');
  function handleSort(col) { setPage(1); if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); else { setSortCol(col); setSortDir('asc'); } }

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE), search, sort: sortCol, dir: sortDir, ...(line ? { line } : {}) });
      const { data } = await axios.get(`${API}/new-today?${params}`);
      setData(data);
    } catch (e) { toast.error('Could not load'); }
  }, [search, page, sortCol, sortDir, line]);
  useEffect(() => { load(); }, [load]);

  const rows = (data?.rows || []).filter(r => {
    if (line) return true;                 // server already filtered to the chosen partner line
    if (lineKind === 'all') return true;
    const found = Object.keys(r.line_presence || {}).filter(k => r.line_presence[k]);
    return found.some(k => lineKindOf(lineById(k)) === lineKind);
  });

  return (
    <div>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 10px' }}>New bundles since the last fetch — only those not yet in a whitelist or blocklist.</p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <select className="select-field" value={lineKind} onChange={e => setLineKind(e.target.value)} style={{ width: 150 }}>
          {LINE_KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
        </select>
        <select className="select-field" value={line} onChange={e => { setPage(1); setLine(e.target.value); }} style={{ minWidth: 170 }} title="Filter to a partner line">
          <option value="">All partner lines</option>
          {linkedDemand.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <input className="input-field" placeholder="Search new bundles…" value={search} onChange={e => { setPage(1); setSearch(e.target.value); }} style={{ flex: 1, minWidth: 140 }} />
        <CopyMenu disabled={!rows.length} onPick={async (kind) => {
          const params = new URLSearchParams({ search, lineKind, export: kind, ...(line ? { line } : {}) });
          const { data: ex } = await axios.get(`${API}/new-today?${params}`);
          copyToClipboard(ex.values, kind === 'ids' ? 'IDs' : 'domains');
        }} />
      </div>
      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead><tr>
            <SortHeader col="bundle_id" label="Bundle / domain" current={sortCol} dir={sortDir} onSort={handleSort} />
            <SortHeader col="env" label="Env" current={sortCol} dir={sortDir} onSort={handleSort} />
            <SortHeader col="store_url" label="Store" current={sortCol} dir={sortDir} onSort={handleSort} />
            <SortHeader col="developer_domain" label="ads.txt" current={sortCol} dir={sortDir} onSort={handleSort} />
            <th>Privacy Policy</th>
            <SortHeader col="inventory" label="Requests" current={sortCol} dir={sortDir} onSort={handleSort} style={{ textAlign: 'right' }} />
            <SortHeader col="lines_found" label="Lines found" current={sortCol} dir={sortDir} onSort={handleSort} />
            <SortHeader col="state" label="State" current={sortCol} dir={sortDir} onSort={handleSort} />
          </tr></thead>
          <tbody>
            {rows.map(r => {
              const found = Object.keys(r.line_presence || {}).filter(k => r.line_presence[k]);
              const state = r.verify_state === 'removed' ? 'removed' : r.verify_state === 'unverifiable' ? 'unver' : (found.length ? 'auth' : 'unauth');
              return (
                <tr key={r.bundle_id}>
                  <td><div style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.bundle_id}</div><div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.app_name || 'First seen today'}</div></td>
                  <td><EnvBadge env={r.env} /></td>
                  <td><StoreLink row={r} /></td>
                  <td><AdsTxtLink row={r} /></td>
                  <td><PrivacyLink row={r} /></td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(r.inventory || 0).toLocaleString()}</td>
                  <td style={{ whiteSpace: 'normal' }}>{r.verify_state === 'unverifiable'
                    ? <span style={{ color: 'var(--text-secondary)' }}>—</span>
                    : (found.length ? found.map(k => <span key={k} className="badge badge-cyan" style={{ margin: '1px 2px' }}>{nameOf(k)}</span>) : <span className="badge badge-muted">None</span>)}</td>
                  <td><StateDot state={state} /></td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={8} style={{ color: 'var(--text-secondary)', padding: 14 }}>No new bundles.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager meta={data} onPage={setPage} />
    </div>
  );
}

// ─── Missed Opportunities ─────────────────────────────────────────────────────
// Sub-tabbed so more cuts of the same data can land beside Publisher Coverage.
function MissedOppsTab({ lines }) {
  const [sub, setSub] = useState('coverage');
  return (
    <div>
      <div className="tabs" style={{ marginBottom: 14 }}>
        <button className={`tab ${sub === 'coverage' ? 'active' : ''}`} onClick={() => setSub('coverage')}>
          <TrendingUp size={14} /> Publisher Coverage
        </button>
        <button className={`tab ${sub === 'partners' ? 'active' : ''}`} onClick={() => setSub('partners')}>
          <Plug size={14} /> Supply Partner Coverage
        </button>
      </div>
      {sub === 'coverage' && <PublisherCoverage lines={lines} />}
      {sub === 'partners' && <SupplyPartnerCoverage lines={lines} />}
    </div>
  );
}

const COV_VIEWS = [
  { v: 'gaps', label: 'With gaps' },
  { v: 'all',  label: 'All publishers' },
  { v: 'full', label: 'Fully covered' },
  { v: 'none', label: 'No coverage' },
];
const COV_ENVS = [
  { v: 'all',   label: 'All env' },
  { v: 'inapp', label: 'In-app' },
  { v: 'web',   label: 'Web' },
];

function CoverageBar({ pct }) {
  const c = pct >= 80 ? 'var(--green)' : pct >= 40 ? 'var(--amber)' : 'var(--red)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--bg-elevated)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', background: c }} />
      </div>
      <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12, fontWeight: 700, color: c, minWidth: 44, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

// Publisher = developer domain. app-ads.txt is hosted there and is crawled once
// per domain, so coverage is a publisher-level fact: fixing one publisher lifts
// every bundle underneath it.
function PublisherCoverage({ lines }) {
  const [lineKind, setLineKind] = useState('demand');
  const [env, setEnv]           = useState('all');
  const [view, setView]         = useState('gaps');
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [sortCol, setSortCol]   = useState('requests');
  const [sortDir, setSortDir]   = useState('desc');
  const [data, setData]         = useState(null);
  const [open, setOpen]         = useState({});

  function handleSort(col) {
    setPage(1);
    if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('desc'); }
  }

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        lineKind, env, view, search, page: String(page), pageSize: '50', sort: sortCol, dir: sortDir,
      });
      const { data } = await axios.get(`${API}/coverage?${params}`);
      setData(data);
    } catch (e) { toast.error('Could not load coverage'); }
  }, [lineKind, env, view, search, page, sortCol, sortDir]);
  useEffect(() => { load(); }, [load]);

  const s    = data?.summary;
  const rows = data?.rows || [];
  const fmt  = n => Number(n || 0).toLocaleString();
  const nameOf = id => (data?.lines || []).find(l => String(l.id) === String(id))?.name
                    || lines.find(l => String(l.id) === String(id))?.name || `#${id}`;
  const toggle = p => setOpen(o => ({ ...o, [p]: !o[p] }));

  return (
    <div>
      <p className="page-subtitle" style={{ marginTop: 0 }}>
        How much of your line set each publisher carries. app-ads.txt lives on the developer domain and is checked once per domain,
        so every bundle under a publisher shares the same answer — one fix lifts them all.
      </p>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <select className="select-field" value={lineKind} onChange={e => { setPage(1); setLineKind(e.target.value); }} style={{ width: 150 }}>
          <option value="demand">Demand lines</option>
          <option value="yours">Your lines</option>
          <option value="all">All lines</option>
        </select>
        <select className="select-field" value={env} onChange={e => { setPage(1); setEnv(e.target.value); }} style={{ width: 130 }}>
          {COV_ENVS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <select className="select-field" value={view} onChange={e => { setPage(1); setView(e.target.value); }} style={{ width: 160 }}>
          {COV_VIEWS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <input className="input-field" placeholder="Search publisher domain…" value={search}
               onChange={e => { setPage(1); setSearch(e.target.value); }} style={{ flex: '1 1 220px', minWidth: 180 }} />
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
        <MiniStat label="Publishers" value={s ? fmt(s.publishers) : '—'} />
        <MiniStat label="Requests behind gaps" value={s ? fmt(s.requestsBehindGaps) : '—'} color="var(--red)"
                  sub={s ? `${s.totalLines} line${s.totalLines === 1 ? '' : 's'} in set` : ''} />
        <MiniStat label="Fully covered" value={s ? fmt(s.fullyCovered) : '—'} color="var(--green)" />
        <MiniStat label="No coverage" value={s ? fmt(s.zeroCoverage) : '—'} color="var(--amber)" />
      </div>

      {/* Honest accounting — never present a partial view as complete. */}
      {s && (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 0, marginBottom: 12 }}>
          Based on {fmt(s.bundlesCovered)} of {fmt(s.bundlesScanned)} bundles ·
          {' '}{fmt(s.excludedUnverifiable)} unverifiable ·
          {' '}{fmt(s.excludedNoDomain)} with no developer domain (cannot be attributed to a publisher).
        </p>
      )}

      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead><tr>
            <SortHeader col="publisher" label="Publisher" current={sortCol} dir={sortDir} onSort={handleSort} />
            <th>ads.txt</th>
            <SortHeader col="bundles" label="Bundles" current={sortCol} dir={sortDir} onSort={handleSort} style={{ textAlign: 'right' }} />
            <SortHeader col="requests" label="Requests" current={sortCol} dir={sortDir} onSort={handleSort} style={{ textAlign: 'right' }} />
            <SortHeader col="coveragePct" label="Coverage" current={sortCol} dir={sortDir} onSort={handleSort} />
            <SortHeader col="existingCount" label="Existing lines" current={sortCol} dir={sortDir} onSort={handleSort} style={{ textAlign: 'right' }} />
            <SortHeader col="missingCount" label="Missing lines" current={sortCol} dir={sortDir} onSort={handleSort} style={{ textAlign: 'right' }} />
            <th />
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <React.Fragment key={r.publisher}>
                <tr onClick={() => toggle(r.publisher)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.publisher}</td>
                  <td onClick={e => e.stopPropagation()}>
                    {(r.envs && r.envs.length ? r.envs : ['inapp']).map(ev => (
                      <AdsTxtLink key={ev} row={{ env: ev, developer_domain: r.publisher, bundle_id: r.publisher }} />
                    ))}
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.bundles)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.requests)}</td>
                  <td><CoverageBar pct={r.coveragePct} /></td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--green)', fontWeight: 700 }}>{r.existingCount}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.missingCount ? 'var(--red)' : 'var(--text-secondary)', fontWeight: 700 }}>{r.missingCount}</td>
                  <td style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                    {open[r.publisher] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </td>
                </tr>
                {open[r.publisher] && (
                  <tr>
                    <td colSpan={8} style={{ background: 'var(--bg-elevated)', whiteSpace: 'normal' }}>
                      <div style={{ padding: '4px 2px 10px' }}>
                        {r.missingLines.length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 700, marginBottom: 5 }}>MISSING LINES</div>
                            {r.missingLines.map(id => (
                              <span key={id} className="badge badge-red" style={{ margin: '1px 3px 1px 0' }}>{nameOf(id)}</span>
                            ))}
                          </div>
                        )}
                        {r.existingLines.length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 700, marginBottom: 5 }}>EXISTING LINES</div>
                            {r.existingLines.map(id => (
                              <span key={id} className="badge badge-green" style={{ margin: '1px 3px 1px 0' }}>{nameOf(id)}</span>
                            ))}
                          </div>
                        )}
                        <PublisherBundles publisher={r.publisher} total={r.bundles} preview={r.topBundles} env={env} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} style={{ color: 'var(--text-secondary)', padding: 14 }}>
                No publishers match this view. Try “All publishers”, or widen the line set.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pager meta={data} onPage={setPage} />
    </div>
  );
}

// Bundles under one publisher. Starts as the top-10 preview already present in
// the /coverage payload (no request), and only calls /coverage/bundles when the
// user asks for more — so big publishers cost nothing unless someone looks.
function PublisherBundles({ publisher, total, preview, env }) {
  const PS = 25;
  const [data, setData] = useState(null);   // null = still showing the preview
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);

  const fetchPage = useCallback(async (p) => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ publisher, page: String(p), pageSize: String(PS), env: env || 'all' });
      const { data } = await axios.get(`${API}/coverage/bundles?${params}`);
      setData(data);
      setPage(p);
    } catch (e) { toast.error('Could not load bundles'); }
    finally { setBusy(false); }
  }, [publisher, env]);

  const rows = data ? data.rows : (preview || []);
  const shown = data ? `${(data.page - 1) * data.pageSize + 1}–${Math.min(data.page * data.pageSize, data.total)} of ${data.total.toLocaleString()}`
                     : `top ${rows.length} of ${Number(total || 0).toLocaleString()}`;
  const fmt = n => Number(n || 0).toLocaleString();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 700 }}>
          BUNDLES BY REQUESTS ({shown})
        </span>
        {!data && total > rows.length && (
          <button className="btn btn-sm btn-secondary" disabled={busy} onClick={() => fetchPage(1)}>
            {busy ? 'Loading…' : `Load more (${fmt(total)})`}
          </button>
        )}
      </div>

      <table className="data-table" style={{ width: 'auto', minWidth: 420 }}>
        <tbody>
          {rows.map(b => (
            <tr key={b.bundle_id}>
              <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{b.bundle_id}</td>
              <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{b.app_name || ''}</td>
              <td><EnvBadge env={b.env} /></td>
              <td><StoreLink row={b} /></td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(b.inventory)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td style={{ color: 'var(--text-secondary)', padding: 10 }}>No bundles.</td></tr>
          )}
        </tbody>
      </table>

      {data && data.pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <button className="btn btn-sm btn-secondary" disabled={busy || page <= 1} onClick={() => fetchPage(page - 1)}>
            <ChevronLeft size={12} /> Prev
          </button>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Page {data.page} of {data.pages}</span>
          <button className="btn btn-sm btn-secondary" disabled={busy || page >= data.pages} onClick={() => fetchPage(page + 1)}>
            Next <ChevronRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

// Supply Partner Coverage — PHASE 1: the partner list captured from the report
// dimension chosen in Configuration. sellers.json analysis is not wired yet: a
// partner NAME here is not a sellers.json URL, and that name→domain mapping has
// to be decided rather than guessed, so the tab states plainly what it can and
// cannot do instead of showing numbers it cannot stand behind.

const PC_VIEWS = [
  { v: 'missing',   label: 'Missing traffic' },
  { v: 'receiving', label: 'Receiving traffic' },
  { v: 'all',       label: 'All publishers' },
];

// "3 days ago" reads faster than a timestamp when the only question is "is this
// fresh?". Exact time stays on hover.
function agoText(iso) {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return 'never';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 2)    return 'just now';
  if (mins < 60)   return `${mins} minutes ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)    return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1)  return 'yesterday';
  if (days < 30)   return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

const PC_STATUS = {
  declared:      { label: 'Declared',      color: 'var(--green)' },
  type_mismatch: { label: 'Type mismatch', color: 'var(--amber)' },
  not_declared:  { label: 'Not declared',  color: 'var(--red)' },
  unreachable:   { label: 'Unreachable',   color: 'var(--text-secondary)' },
  error:         { label: 'Error',         color: 'var(--text-secondary)' },
};

// Supply Partner Coverage, scoped to the partners THIS company receives from.
// For each one: every publisher in their sellers.json, matched against the
// inventory that actually arrives through that partner.
function SupplyPartnerCoverage({ lines }) {
  const [partners, setPartners] = useState(null);
  const [loading, setLoading]   = useState(true);   // null partners alone cannot
  const [loadErr, setLoadErr]   = useState('');     // distinguish loading from failed
  const [partner, setPartner]   = useState('');
  const [view, setView]         = useState('missing');
  const [lineCov, setLineCov]   = useState('all');
  const [lineId, setLineId]     = useState('');   // '' = any of your lines
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [sortCol, setSortCol]   = useState('requests');
  const [sortDir, setSortDir]   = useState('desc');
  const [data, setData]         = useState(null);
  const [verified, setVerified] = useState({});
  const [busy, setBusy]         = useState('');

  useEffect(() => {
    axios.get(`${API}/supply-partners`)
      .then(({ data }) => {
        setPartners(data);
        setLoadErr('');
        const first = (data.partners || []).find(p => p.sellersDomain);
        if (first) setPartner(first.partnerId);
      })
      .catch(e => {
        // Surface the real reason IN THE PAGE. A toast can vanish before it is
        // read, and this component previously rendered nothing at all on failure.
        // errText guarantees a string — {loadErr} is rendered directly.
        setLoadErr(errText(e, 'Could not load supply partners'));
      })
      .finally(() => setLoading(false));
  }, []);

  const current = partners && (partners.partners || []).find(p => p.partnerId === partner);

  const load = useCallback(async () => {
    if (!current || !current.sellersDomain) { setData(null); return; }
    try {
      const params = new URLSearchParams({
        source: current.sellersDomain, partner, view, lineCov, lineId, search,
        page: String(page), pageSize: '50', sort: sortCol, dir: sortDir,
      });
      const { data } = await axios.get(`${API}/partner-coverage?${params}`);
      setData(data);
    } catch (e) {
      toast.error(errText(e, 'Could not load coverage'));
      setData(null);
    }
  // eslint-disable-next-line
  }, [partner, current && current.sellersDomain, view, lineCov, lineId, search, page, sortCol, sortDir]);
  useEffect(() => { load(); }, [load]);

  function handleSort(col) {
    setPage(1);
    if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('desc'); }
  }

  // Exports the WHOLE filtered set via the same endpoint (format=csv), so the
  // file can never disagree with what the filters are showing.
  const downloadCsv = () => {
    if (!current || !current.sellersDomain) return;
    const params = new URLSearchParams({
      source: current.sellersDomain, partner, view, lineCov, lineId, search,
      sort: sortCol, dir: sortDir, format: 'csv',
    });
    window.open(`${API}/partner-coverage?${params}`, '_blank');
  };

  // Crawl this partner's publishers for your lines + their seller_id. Backgrounded
  // server-side; results land in the two columns as rows are written.
  const runPublisherCheck = async () => {
    if (!current || !current.sellersDomain) return;
    setBusy('check');
    try {
      // NO source: check EVERY mapped partner in one pass. The job dedupes publisher
      // domains across sources and asks every partner's question in a single fetch,
      // so an unscoped run is both complete AND cheaper than running it once per
      // partner (a shared publisher would otherwise be crawled twice).
      await axios.post(`${API}/publisher-check`, {});
      toast.success('Crawling every publisher across all your mapped partners — this can take several minutes.');

      // The crawl is long. Poll the checked count and stop when it stops moving,
      // instead of reloading once far too early and looking like nothing happened.
      let last = -1, still = 0;
      const poll = setInterval(async () => {
        try {
          const params = new URLSearchParams({
            source: current.sellersDomain, partner, view, lineCov, lineId, search,
            page: String(page), pageSize: '50', sort: sortCol, dir: sortDir,
          });
          const { data: d } = await axios.get(`${API}/partner-coverage?${params}`);
          setData(d);
          const n = (d.summary && d.summary.checked) || 0;
          still = (n === last) ? still + 1 : 0;
          last = n;
          if (still >= 3) { clearInterval(poll); setBusy(''); toast.success(`Done — ${n.toLocaleString()} publishers checked`); }
        } catch { clearInterval(poll); setBusy(''); }
      }, 10000);
    } catch (e) {
      toast.error(errText(e, 'Could not start the check'));
      setBusy('');
    }
  };

  // Re-pull just the ad-server report so the bundle x partner split reflects the
  // current dimension / ID->name mapping. Deliberately NOT the full "Run now",
  // which also re-crawls every developer domain.
  // The server answers 202 and works in the background, so poll run-status
  // rather than holding the request open (which is what caused the 502).
  const refetchPartners = async () => {
    setBusy('fetch');
    try {
      await axios.post(`${API}/run-fetch`, {});
      toast.success('Fetching from your ad server — this can take a few minutes.');

      const started = Date.now();
      const poll = setInterval(async () => {
        try {
          const { data: st } = await axios.get(`${API}/run-status`);
          if (st.status === 'running' && Date.now() - started < 15 * 60 * 1000) return;
          clearInterval(poll);
          setBusy('');
          if (st.status === 'error') toast.error(`Fetch failed: ${String(st.error || 'unknown')}`);
          else toast.success('Partner data refreshed');
          const { data: p } = await axios.get(`${API}/supply-partners`);
          setPartners(p);
        } catch {
          clearInterval(poll);
          setBusy('');
        }
      }, 4000);
    } catch (e) {
      toast.error(errText(e, 'Could not start the fetch'));
      setBusy('');
    }
  };

  // Live ads.txt reciprocity for the rows on screen. Bounded — each is a real
  // third-party fetch.
  const verifyVisible = async () => {
    const rows = (data && data.rows) || [];
    const batch = rows.slice(0, 25).map(r => ({ seller_id: r.seller_id, seller_domain: r.seller_domain }));
    if (!batch.length) return;
    setBusy('verify');
    try {
      const { data: out } = await axios.post(`${API}/partner-coverage/verify`,
        { source: current.sellersDomain, sellers: batch });
      const next = { ...verified };
      (out.results || []).forEach(r => { next[`${r.seller_domain}|${r.seller_id}`] = r; });
      setVerified(next);
      toast.success(`Checked ${out.checked} publisher${out.checked === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(errText(e, 'Verify failed'));
    } finally { setBusy(''); }
  };

  // Only the company's OWN lines are searched by the publisher check, so the
  // filter offers exactly those.
  const yoursLines = (lines || []).filter(l => (l && l.kind) === 'yours');
  const s = data && data.summary;
  const fmt = n => Number(n || 0).toLocaleString();
  const rows = (data && data.rows) || [];
  const list = (partners && partners.partners) || [];

  return (
    <div>
      <p className="page-subtitle" style={{ marginTop: 0 }}>
        For each of your supply partners: every publisher in their sellers.json, matched against the inventory
        you actually receive through them. Publishers they work with that send you nothing are the opportunity.
      </p>

      {loading && (
        <div style={{ padding: 16, color: 'var(--text-secondary)' }}>Loading your supply partners…</div>
      )}

      {!loading && loadErr && (
        <div className="card" style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--red)' }}>Could not load supply partners</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {loadErr}
            <br />
            If this says 404, the backend deploy has not finished yet — reload in a minute.
          </div>
        </div>
      )}

      {!loading && !loadErr && list.length === 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>No supply partners yet</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Add them under <strong>Configuration &amp; Settings → Supply partners to analyse</strong>, or set a
            <strong> Supply partner dimension</strong> and let the nightly run discover them.
          </div>
        </div>
      )}

      {!loading && !loadErr && list.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <select className="select-field" value={partner}
                    onChange={e => { setPage(1); setVerified({}); setPartner(e.target.value); }} style={{ width: 260 }}>
              {list.map(p => (
                <option key={p.partnerId} value={p.partnerId}>
                  {p.partnerName}{p.nameMapped ? ` (${p.partnerId})` : ''}
                  {p.sellersDomain ? ` → ${p.sellersDomain}` : ' — not mapped'}
                </option>
              ))}
            </select>
            <select className="select-field" value={view} onChange={e => { setPage(1); setView(e.target.value); }} style={{ width: 165 }}>
              {PC_VIEWS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <select className="select-field" value={lineId}
                    onChange={e => { setPage(1); setLineId(e.target.value); }} style={{ width: 190 }}>
              <option value="">All your lines</option>
              {yoursLines.map(l => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
            </select>
            <select className="select-field" value={lineCov}
                    onChange={e => { setPage(1); setLineCov(e.target.value); }} style={{ width: 165 }}>
              <option value="all">Any coverage</option>
              <option value="has">Carries</option>
              <option value="missing">Missing</option>
              <option value="unchecked">Not checked yet</option>
            </select>
            <input className="input-field" placeholder="Search publisher domain or seller ID…" value={search}
                   onChange={e => { setPage(1); setSearch(e.target.value); }} style={{ flex: '1 1 200px', minWidth: 170 }} />
            <button className="btn btn-secondary" disabled={busy === 'verify' || rows.length === 0} onClick={verifyVisible}>
              {busy === 'verify' ? 'Checking…' : `Verify ads.txt (${Math.min(rows.length, 25)})`}
            </button>
            <button className="btn btn-secondary" disabled={!current || !current.sellersDomain || rows.length === 0}
                    onClick={downloadCsv} title="Download every row matching the current filters, not just this page.">
              Download CSV
            </button>
            <button className="btn btn-secondary" disabled={busy === 'check'} onClick={runPublisherCheck}
                    title="Crawl every publisher across ALL your mapped partners — including ones sending you nothing — for your lines and each partner's seller_id. Publishers shared between partners are fetched once.">
              {busy === 'check' ? 'Checking…' : 'Check all partners'}
            </button>
            <button className="btn btn-secondary" disabled={busy === 'fetch'} onClick={refetchPartners}
                    title="Re-pull the ad-server report and rebuild the bundle × partner split. Does NOT re-crawl ads.txt.">
              {busy === 'fetch' ? 'Fetching…' : 'Refresh partner data'}
            </button>
          </div>

          {current && !current.sellersDomain && (
            <div className="card" style={{ padding: 16, marginBottom: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                “{current.partnerName}” has no sellers.json mapped
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {!current.nameMapped
                  ? <>Your ad server reports this partner as <strong>{current.partnerId}</strong> with no name attached.
                      Add the ID and its real name under <strong>Configuration &amp; Settings → Supply partners to analyse</strong>,
                      then an admin can map that name in the <strong>Sellers.json DB</strong>.</>
                  : <>An admin needs to add this partner in the <strong>Sellers.json DB</strong> — with
                      <strong> “{current.partnerName}”</strong> as one of its aliases — and fetch it.</>}
              </div>
            </div>
          )}

          {s && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 8 }}>
                {/* The table is one row per seller ID, and a publisher may hold
                    several. Show the entry count (what the rows below total) with the
                    distinct publisher count underneath (what actually gets crawled). */}
                <MiniStat label="Seller entries in their sellers.json" value={fmt(s.total)}
                          sub={s.publishers != null && s.publishers !== s.total
                            ? `${fmt(s.publishers)} unique publisher domains`
                            : undefined} />
                <MiniStat label={data.scope === 'partner' ? 'Reaching you via this partner' : 'You receive (any partner)'}
                          value={fmt(s.receiving)} color="var(--green)" />
                {/* Dead entries are netted OUT of the headline. A domain that does
                    not resolve is not an opportunity, and leaving it in both inflates
                    the number and buries the finding that the partner's list is
                    stale. The count is still shown — as its own, more useful line. */}
                <MiniStat label="Reachable but sending nothing"
                          value={fmt(Math.max(0, Number(s.missing || 0) - Number(s.deadMissing || 0)))}
                          color="var(--amber)"
                          sub={s.total
                            ? `${Math.round(((Number(s.missing || 0) - Number(s.deadMissing || 0)) / s.total) * 100)}% of their supply`
                            + (s.deadMissing ? ` · ${fmt(s.deadMissing)} dead entries excluded` : '')
                            : ''} />
                <MiniStat label="Requests you already get" value={fmt(s.requestsReceiving)} />
              </div>
              {/* Two findings, deliberately separate: their list is wrong vs their
                  publishers are unverifiable. Merged into "missing", neither is
                  legible — and the first one is the partner's problem, not yours. */}
              {(s.deadMissing > 0 || s.nofileMissing > 0) && (
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12,
                              color: 'var(--text-secondary)', marginTop: 8, marginBottom: 4,
                              padding: '8px 12px', background: 'var(--bg-surface)',
                              border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                  {s.deadMissing > 0 && (
                    <span title="These seller_domains do not resolve, refuse connections, or are not valid domains. They cannot send you anything — this is a data-quality problem in the partner's sellers.json.">
                      <strong style={{ color: 'var(--red)' }}>{fmt(s.deadMissing)}</strong> dead entries in their list
                      {s.total ? ` (${Math.round((s.deadMissing / s.total) * 100)}%)` : ''} — raise with the partner
                    </span>
                  )}
                  {s.nofileMissing > 0 && (
                    <span title="These publishers resolve and answer, but have no app-ads.txt at all — so they authorise nobody, and no buyer can verify their inventory.">
                      <strong style={{ color: 'var(--amber)' }}>{fmt(s.nofileMissing)}</strong> publish no app-ads.txt at all
                    </span>
                  )}
                </div>
              )}

              {/* Be explicit about which scope produced these numbers. */}
              {/* Say how much of this partner has actually been crawled — otherwise
                  "not checked" rows look like a failure rather than pending work. */}
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 0, marginBottom: 6 }}>
                {/* THREE states, not two: answered / fetch failed / never asked.
                    Lumping the failures in with "not checked" tells the reader to press
                    the button again, which cannot help — the publisher's file did not
                    load. */}
                {(() => {
                  const unreach = Number(s.unreachable || 0);
                  const notAsked = Math.max(0, Number(s.total || 0) - Number(s.checked || 0) - unreach);
                  return (
                    <>
                      <strong>{fmt(s.checked)}</strong> of {fmt(s.total)} entries answered for this partner.
                      {unreach > 0 ? <> <strong>{fmt(unreach)}</strong> could not be read — the publisher’s
                        app-ads.txt did not load (dead domain, timeout, or not a text file), so there is no answer
                        to give. That is different from “not declared”, and re-running will not change it.</> : null}
                      {notAsked > 0
                        ? <> {fmt(notAsked)} not asked yet
                            {busy === 'check' ? ' — crawling…' : ' — press “Check all partners”.'}</>
                        : null}
                    </>
                  );
                })()}
                {/* Say WHEN, not just how many. Without this, answers gathered once by
                    hand three days ago look identical to ones refreshed today. */}
                {s.lastCheckedAt && (
                  <> Last checked <strong title={new Date(s.lastCheckedAt).toLocaleString()}>{agoText(s.lastCheckedAt)}</strong>
                    {s.oldestCheckedAt && s.oldestCheckedAt !== s.lastCheckedAt
                      ? <> · oldest row {agoText(s.oldestCheckedAt)}</> : null}.</>)}
              </p>
              <p style={{ fontSize: 12, color: data.scope === 'partner' ? 'var(--text-secondary)' : 'var(--amber)',
                          marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
                {data.scope === 'partner'
                  ? <>Counting only inventory received <strong>via {data.partner}</strong>.</>
                  : <>Counting <strong>all your inventory</strong>, not just what arrives via {data.partner || 'this partner'} —
                     per-partner traffic is recorded from the first nightly run after a Supply partner dimension is set.</>}
              </p>
            </>
          )}

          {current && current.sellersDomain && (
            <div className="card" style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead><tr>
                  <SortHeader col="seller_domain" label="Publisher" current={sortCol} dir={sortDir} onSort={handleSort} />
                  <SortHeader col="seller_id" label="Seller ID" current={sortCol} dir={sortDir} onSort={handleSort} />
                  <th>Type</th>
                  <SortHeader col="bundles" label="Receiving bundles" current={sortCol} dir={sortDir} onSort={handleSort} style={{ textAlign: 'right' }} />
                  <SortHeader col="requests" label="Receiving requests" current={sortCol} dir={sortDir} onSort={handleSort} style={{ textAlign: 'right' }} />
                  <th>Partner&apos;s ads.txt coverage</th>
                  <th>{lineId
                    ? `${(yoursLines.find(l => String(l.id) === String(lineId)) || {}).name || 'Selected line'}`
                    : 'Your line coverage'}</th>
                </tr></thead>
                <tbody>
                  {rows.map(r => {
                    const v = verified[`${r.seller_domain}|${r.seller_id}`];
                    const st = v && (PC_STATUS[v.status] || PC_STATUS.error);
                    return (
                      <tr key={`${r.seller_domain}|${r.seller_id}`}>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                          {r.seller_domain || '—'}
                          {r.name && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.name}</div>}
                        </td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.seller_id}</td>
                        <td style={{ fontSize: 12 }}>{r.seller_type}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.bundles ? 'var(--text-primary)' : 'var(--amber)' }}>
                          {r.bundles ? fmt(r.bundles) : 'none'}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.requests ? fmt(r.requests) : '—'}</td>
                        {/* Live Verify wins when present — it was fetched just now.
                            Otherwise fall back to the stored nightly check. */}
                        <td>
                          {v
                            ? <span title={v.message || v.foundIn || ''} style={{ color: st.color, fontSize: 12, fontWeight: 600 }}>
                                {st.label}{v.foundIn ? ` · ${v.foundIn}` : ''}
                              </span>
                            : r.checked
                              ? (!r.checkOk
                                  ? <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Unreachable</span>
                                  /* Crawled, but never asked about THIS partner's seller ID.
                                     Red "Not declared" here would assert a reciprocity failure
                                     nobody observed. */
                                  : !r.partnerAsked
                                    ? <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}
                                            title="This publisher was crawled, but not for this partner's seller ID. Press Check publishers to ask.">
                                        not asked yet
                                      </span>
                                    : <span style={{ color: r.partnerDeclared ? 'var(--green)' : 'var(--red)', fontSize: 12, fontWeight: 600 }}>
                                        {r.partnerDeclared ? 'Declared' : 'Not declared'}
                                      </span>)
                              : <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>not checked</span>}
                        </td>
                        {/* With a line selected, answer for THAT line; otherwise the
                            N/M summary across all your lines. */}
                        <td style={{ fontSize: 12 }}>
                          {!r.checked
                            ? <span style={{ color: 'var(--text-secondary)' }}>not checked</span>
                            : !r.checkOk
                              ? <span style={{ color: 'var(--text-secondary)' }}>unreachable</span>
                              : lineId
                                ? <span style={{ fontWeight: 700, color: r.lineHit ? 'var(--green)' : 'var(--red)' }}>
                                    {r.lineHit ? 'Carries' : 'Missing'}
                                  </span>
                                : <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                                                 color: r.yourLinesFound > 0 ? 'var(--green)' : 'var(--red)' }}>
                                    {r.yourLinesFound} / {r.yourLinesTotal}
                                  </span>}
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr><td colSpan={7} style={{ color: 'var(--text-secondary)', padding: 14 }}>Nothing to show for this view.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {current && current.sellersDomain && <Pager meta={data} onPage={setPage} />}

          <div style={{ marginTop: 20, fontSize: 12, color: 'var(--text-secondary)' }}>
            {list.length} supply partner{list.length === 1 ? '' : 's'} ·{' '}
            {list.filter(p => p.sellersDomain).length} mapped to a sellers.json source ·{' '}
            {partners && partners.partnerDataAvailable
              ? 'per-partner traffic recorded'
              : 'per-partner traffic arrives after the next nightly run'}
          </div>
        </>
      )}
    </div>
  );
}
